<template>
  <div class="patient-data-simulator">
    <h2>甲状腺结节微波治疗后数据模拟器</h2>

    <!-- 数据管理按钮 -->
    <div class="management-buttons">
      <button @click="showDataManager = !showDataManager" class="manage-btn">
        {{ showDataManager ? "隐藏数据管理" : "管理基础数据" }}
      </button>
      <button @click="exportData" class="export-btn">导出数据</button>
      <button @click="importData" class="import-btn">导入数据</button>
      <button @click="triggerExcelImport" class="excel-import-btn">
        批量导入Excel
      </button>
      <input
        type="file"
        ref="excelFileInput"
        @change="handleExcelImport"
        accept=".xls,.xlsx"
        style="display: none"
      />
      <input
        type="file"
        ref="fileInput"
        @change="handleFileImport"
        accept=".json"
        style="display: none"
      />
    </div>

    <!-- 基础数据管理 -->
    <div v-if="showDataManager" class="data-management-section">
      <h3>基础数据管理</h3>
      <div class="management-controls">
        <button @click="addNewPatient" class="add-btn">新增患者</button>
        <button @click="saveTrainingData" class="save-btn">保存数据</button>
        <button @click="resetTrainingData" class="reset-btn">重置数据</button>
      </div>

      <div class="training-data-list">
        <div
          v-for="(patient, index) in trainingData"
          :key="index"
          class="patient-item"
        >
          <div class="patient-header">
            <input
              v-model="patient.name"
              class="patient-name-input"
              placeholder="患者姓名"
            />
            <button @click="removePatient(index)" class="remove-btn">
              删除
            </button>
          </div>
          <div class="patient-data-inputs">
            <div
              class="month-data-input"
              v-for="month in allMonths"
              :key="month"
            >
              <label>{{ month === 0 ? "术前" : `术后${month}月` }}:</label>
              <div class="dimension-inputs">
                <input
                  type="number"
                  :value="getPatientDimensionValue(patient, month, 'length')"
                  @input="
                    updatePatientDimension(
                      patient,
                      month,
                      'length',
                      $event.target.value
                    )
                  "
                  placeholder="长"
                  min="1"
                  class="dimension-input small"
                />
                <span class="separator">×</span>
                <input
                  type="number"
                  :value="getPatientDimensionValue(patient, month, 'width')"
                  @input="
                    updatePatientDimension(
                      patient,
                      month,
                      'width',
                      $event.target.value
                    )
                  "
                  placeholder="宽"
                  min="1"
                  class="dimension-input small"
                />
                <span class="separator">×</span>
                <input
                  type="number"
                  :value="getPatientDimensionValue(patient, month, 'height')"
                  @input="
                    updatePatientDimension(
                      patient,
                      month,
                      'height',
                      $event.target.value
                    )
                  "
                  placeholder="高"
                  min="1"
                  class="dimension-input small"
                />
                <span class="unit">mm</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 基础数据统计 -->
    <div class="input-section">
      <h3>基础训练数据 ({{ trainingData.length }} 名患者)</h3>
      <div class="stats">
        <div class="data-summary">
          <div class="summary-item">
            <span class="label">平均术前尺寸:</span>
            <span class="value">{{ getAverageSize(0).join(" × ") }} mm</span>
          </div>
          <div class="summary-item">
            <span class="label">平均术后12月尺寸:</span>
            <span class="value">{{ getAverageSize(12).join(" × ") }} mm</span>
          </div>
          <div class="summary-item">
            <span class="label">数据完整性:</span>
            <span class="value">{{ calculateDataCompleteness() }}%</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 新患者数据输入 -->
    <div class="new-patient-section">
      <h3>新患者数据补全</h3>
      <div class="patient-input">
        <label>患者姓名: </label>
        <input
          v-model="newPatient.name"
          placeholder="输入患者姓名"
          class="name-input"
        />
      </div>

      <div class="month-inputs">
        <div class="month-input preoperative">
          <label>硬化前(术前): </label>
          <div class="dimension-inputs">
            <input
              type="number"
              v-model.number="newPatient.dimensions[0].length"
              placeholder="长"
              min="1"
              class="dimension-input"
            />
            <span class="separator">×</span>
            <input
              type="number"
              v-model.number="newPatient.dimensions[0].width"
              placeholder="宽"
              min="1"
              class="dimension-input"
            />
            <span class="separator">×</span>
            <input
              type="number"
              v-model.number="newPatient.dimensions[0].height"
              placeholder="高"
              min="1"
              class="dimension-input"
            />
            <span class="unit">mm</span>
          </div>
        </div>

        <div v-for="month in postMonths" :key="month" class="month-input">
          <label>术后{{ month }}月: </label>
          <div class="dimension-inputs">
            <input
              type="number"
              v-model.number="newPatient.dimensions[month].length"
              placeholder="长"
              min="1"
              class="dimension-input"
            />
            <span class="separator">×</span>
            <input
              type="number"
              v-model.number="newPatient.dimensions[month].width"
              placeholder="宽"
              min="1"
              class="dimension-input"
            />
            <span class="separator">×</span>
            <input
              type="number"
              v-model.number="newPatient.dimensions[month].height"
              placeholder="高"
              min="1"
              class="dimension-input"
            />
            <span class="unit">mm</span>
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <button @click="simulateMissingData" class="simulate-btn">
          模拟缺失数据
        </button>
        <button @click="clearAllData" class="clear-btn">清空输入</button>
        <button
          @click="saveAsTrainingData"
          class="save-training-btn"
          :disabled="!canSaveAsTrainingData"
        >
          保存为训练数据
        </button>
      </div>
    </div>

    <!-- 结果显示 -->
    <div v-if="simulatedResult" class="result-section">
      <h3>模拟结果 - {{ simulatedResult.name }}</h3>

      <!-- 相似患者信息 -->
      <div v-if="similarPatients.length > 0" class="similar-patients">
        <h4>基于相似患者数据</h4>
        <div class="similar-list">
          <div
            v-for="patient in similarPatients"
            :key="patient.name"
            class="similar-patient"
          >
            <span class="patient-name">{{ patient.name }}</span>
            <span class="similarity"
              >相似度: {{ Math.round(patient.similarity * 100) }}%</span
            >
          </div>
        </div>
      </div>

      <div class="result-data">
        <div class="result-item preoperative">
          <span class="month-label">硬化前(术前):</span>
          <span
            :class="{
              'original-data': hasOriginalData(0),
              'simulated-data': !hasOriginalData(0),
            }"
          >
            {{ formatDimensions(simulatedResult.data[0]) }}
            <span v-if="!hasOriginalData(0)" class="simulated-badge"
              >(模拟)</span
            >
          </span>
        </div>
        <div v-for="month in postMonths" :key="month" class="result-item">
          <span class="month-label">术后{{ month }}月:</span>
          <span
            :class="{
              'original-data': hasOriginalData(month),
              'simulated-data': !hasOriginalData(month),
            }"
          >
            {{ formatDimensions(simulatedResult.data[month]) }}
            <span v-if="!hasOriginalData(month)" class="simulated-badge"
              >(模拟)</span
            >
          </span>
        </div>
      </div>

      <!-- 恢复趋势图表 -->
      <div class="trend-chart">
        <h4>恢复趋势</h4>
        <div class="chart-container">
          <div
            class="chart-bar"
            v-for="(size, month) in simulatedResult.data"
            :key="month"
          >
            <div class="bar-label">
              {{ month === 0 ? "术前" : `术后${month}月` }}
            </div>
            <div class="bar-container">
              <div
                class="bar"
                :style="{ height: calculateBarHeight(size) + '%' }"
                :class="{ 'simulated-bar': !hasOriginalData(month) }"
              ></div>
            </div>
            <div class="bar-value">
              {{ formatDimensions(size) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 数据统计 -->
      <div class="data-stats">
        <h4>数据统计</h4>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-label">体积减少率:</span>
            <span class="stat-value">{{ calculateVolumeReduction() }}%</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">最大尺寸减少:</span>
            <span class="stat-value"
              >{{ calculateMaxDimensionReduction() }}%</span
            >
          </div>
          <div class="stat-item">
            <span class="stat-label">模拟数据点:</span>
            <span class="stat-value">{{ countSimulatedData() }} 个</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const XLSX = require("xlsx");
// const fs = require("fs");
// 在组件外部定义常量
const POST_MONTHS = [1, 3, 6, 9, 12];
const STORAGE_KEY = "thyroid_nodule_training_data";
const ALL_MONTHS = [0, ...POST_MONTHS];
// 默认训练数据
const DEFAULT_TRAINING_DATA = [
  // 完整数据患者
  {
    name: "林宝红",
    data: {
      0: [36, 28, 24],
      1: [24, 17, 13],
      3: [16, 11, 11],
      6: [14, 9, 10],
      9: [13, 8, 9],
    },
  },
  {
    name: "刘飘",
    data: {
      0: [39, 21, 16],
      1: [17, 12, 12],
      3: [12, 10, 8],
      9: [9, 5, 7],
      12: [9, 6, 6],
    },
  },
  {
    name: "王吉华",
    data: {
      0: [36, 30, 32],
      1: [23, 17, 20],
      3: [15, 14, 17],
      9: [10, 9, 11],
      12: [7, 5, 5],
    },
  },
  {
    name: "黄清",
    data: {
      0: [56, 42, 24],
      1: [40, 33, 23],
      3: [37, 28, 22],
      6: [33, 24, 20],
      12: [31, 21, 16],
    },
  },
  {
    name: "吴红蓉",
    data: {
      0: [29, 19, 16],
      1: [15, 15, 7],
      3: [11, 9, 4],
      6: [8, 4, 4],
      12: [4, 4, 3],
    },
  },
  {
    name: "杜娟",
    data: {
      0: [44, 50, 37],
      1: [29, 27, 16],
      3: [22, 20, 13],
      6: [19, 16, 12],
      12: [9, 8, 6],
    },
  },
  {
    name: "冯烨林",
    data: {
      0: [44, 35, 23],
      1: [26, 22, 18],
      3: [21, 19, 15],
      6: [18, 15, 11],
      12: [10, 10, 8],
    },
  },
  {
    name: "陈伟昌",
    data: { 0: [52, 30, 20], 3: [22, 14, 14], 6: [16, 9, 7], 12: [9, 6, 4] },
  },
  {
    name: "戴亚兰",
    data: {
      0: [42, 33, 32],
      1: [10, 9, 7],
      3: [8, 7, 5],
      6: [6, 4, 4],
      12: [5, 3, 3],
    },
  },
  {
    name: "刘婷婷",
    data: {
      0: [32, 24, 18],
      1: [12, 7, 10],
      3: [12, 11, 6],
      6: [10, 8, 5],
      12: [9, 8, 6],
    },
  },
  {
    name: "朱丹枫",
    data: {
      0: [36, 33, 28],
      1: [32, 28, 20],
      3: [28, 22, 19],
      6: [23, 18, 16],
      12: [17, 13, 11],
    },
  },

  // 部分数据患者
  { name: "万海苑", data: { 0: [53, 44, 32], 3: [16, 14, 10] } },
  { name: "何淑慧", data: { 0: [38, 31, 20] } },
  { name: "李群", data: { 0: [45, 34, 24], 1: [20, 15, 9] } },
  { name: "邱碧兰", data: { 0: [47, 39, 28] } },
  { name: "胡菊英", data: { 0: [38, 38, 29], 1: [25, 22, 17] } },
  { name: "郑雪敏", data: { 0: [52, 41, 27], 1: [31, 28, 17] } },
  { name: "张卫芳", data: { 0: [34, 22, 21], 1: [24, 15, 11], 3: [14, 9, 7] } },
  { name: "林巧笑", data: { 0: [49, 40, 56], 1: [38, 32, 29] } },
  {
    name: "牟迎春",
    data: { 0: [35, 31, 21], 1: [24, 17, 13], 3: [21, 14, 10] },
  },
  {
    name: "田相瑞",
    data: { 0: [52, 42, 32], 1: [25, 17, 15], 3: [19, 14, 13] },
  },
  { name: "高莲霞", data: { 0: [24, 20, 13] } },
  { name: "刘翠芬", data: { 0: [39, 31, 20], 1: [23, 21, 16] } },
  { name: "刘静", data: { 0: [45, 32, 15] } },
  { name: "戴长年", data: { 0: [44, 38, 30] } },
  { name: "刘玉珍", data: { 0: [45, 36, 23] } },
  { name: "邹新花", data: { 0: [40, 31, 25] } },
  { name: "廖爱民", data: { 0: [58, 37, 32], 1: [28, 16, 18] } },
  { name: "林晓霞", data: { 0: [39, 24, 31], 1: [22, 18, 12] } },
  { name: "李玉平", data: { 0: [38, 26, 22] } },
  { name: "黄俊龙", data: { 0: [59, 40, 29], 1: [32, 21, 16] } },
  {
    name: "杨仕侠",
    data: { 0: [33, 27, 19], 1: [25, 14, 17], 9: [17, 10, 12] },
  },
  { name: "钟虹莉", data: { 0: [28, 16, 9], 1: [13, 11, 5] } },
  { name: "林海珊", data: { 0: [37, 32, 23], 1: [24, 13, 17] } },
  { name: "罗翠珍", data: { 0: [26, 25, 14], 1: [20, 20, 13] } },
  { name: "李秋玲", data: { 0: [36, 19, 16], 1: [15, 12, 11] } },
  { name: "陈婷婷", data: { 0: [50, 38, 27], 1: [36, 27, 22] } },
  {
    name: "呙艳",
    data: {
      0: [44, 32, 23],
      1: [35, 26, 20],
      3: [31, 19, 19],
      6: [23, 18, 15],
    },
  },
  { name: "黄华英", data: { 0: [31, 27, 16], 1: [19, 18, 14], 9: [9, 11, 6] } },
  { name: "权桂荣", data: { 0: [44, 30, 23], 1: [17, 23, 10], 12: [6, 4, 6] } },
  {
    name: "林金芳",
    data: {
      0: [43, 26, 21],
      1: [29, 28, 15],
      6: [24, 20, 15],
      12: [22, 13, 12],
    },
  },
  { name: "师娇", data: { 0: [52, 35, 25], 1: [36, 15, 21] } },
  { name: "曾祖浩", data: { 0: [43, 26, 37], 1: [28, 21, 16] } },
  { name: "吴秋霞", data: { 0: [44, 38, 20] } },
  { name: "叶荟", data: { 0: [42, 28, 19], 1: [28, 19, 15], 3: [24, 17, 14] } },
  {
    name: "陈泽磷",
    data: {
      0: [50, 36, 32],
      1: [38, 27, 23],
      3: [29, 21, 20],
      6: [28, 25, 20],
      12: [27, 23, 20],
    },
  },
  {
    name: "黄飘飘",
    data: { 0: [45, 39, 26], 3: [25, 19, 12], 12: [8, 9, 13] },
  },
  {
    name: "陶远",
    data: {
      0: [55, 37, 31],
      1: [46, 45, 26],
      3: [33, 31, 27],
      6: [32, 27, 24],
    },
  },
  {
    name: "温雯",
    data: {
      0: [33, 25, 18],
      1: [24, 22, 15],
      3: [20, 17, 14],
      6: [15, 10, 8],
      12: [15, 11, 3],
    },
  },
  { name: "史俊珊", data: { 0: [59, 31, 47], 1: [30, 24, 15] } },
  { name: "甘君", data: { 0: [31, 37, 30], 1: [27, 18, 9], 3: [20, 13, 7] } },
  {
    name: "陈以静",
    data: {
      0: [43, 32, 40],
      1: [21, 19, 18],
      3: [21, 14, 14],
      12: [14, 14, 11],
    },
  },
  {
    name: "杨翠芳",
    data: {
      0: [40, 36, 31],
      1: [30, 20, 16],
      3: [20, 16, 15],
      6: [19, 11, 12],
      12: [14, 10, 11],
    },
  },
  { name: "杨金莲", data: { 0: [41, 29, 20], 3: [20, 15, 8] } },
  { name: "周蒙", data: { 0: [30, 24, 13], 6: [11, 8, 7] } },
  { name: "孙静", data: { 0: [34, 27, 18], 1: [29, 21, 15], 3: [26, 23, 14] } },
  {
    name: "许雪玉",
    data: { 0: [31, 23, 26], 1: [27, 20, 20], 12: [17, 15, 12] },
  },
  {
    name: "周彦彦",
    data: {
      0: [59, 42, 25],
      1: [35, 22, 15],
      3: [27, 19, 13],
      12: [22, 18, 13],
    },
  },
  {
    name: "周海清",
    data: { 0: [38, 22, 31], 1: [23, 19, 10], 3: [19, 14, 8] },
  },
  { name: "陈先转", data: { 0: [37, 30, 26], 1: [31, 22, 22] } },
  { name: "孙雪珍", data: { 0: [33, 22, 20] } },
  { name: "刘静静", data: { 0: [47, 27, 20], 1: [15, 13, 12], 12: [9, 7, 6] } },
  { name: "韦瑞琼", data: { 0: [35, 31, 30], 1: [21, 17, 16] } },
];
// 解析尺寸字符串（如 "53×44×32mm"）
function parseDimensionString(dimStr) {
  if (!dimStr || dimStr === "") return null;
  const match = dimStr.match(/(\d+)×(\d+)×(\d+)mm/);
  if (match) {
    return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
  }
  return null;
}

// 格式化尺寸为字符串
function formatDimensions(dimensions) {
  return `${dimensions[0]}×${dimensions[1]}×${dimensions[2]}mm`;
}

// 计算相似度
function calculateSimilarity(preOp1, preOp2) {
  const distance = Math.sqrt(
    Math.pow(preOp1[0] - preOp2[0], 2) +
      Math.pow(preOp1[1] - preOp2[1], 2) +
      Math.pow(preOp1[2] - preOp2[2], 2)
  );
  const maxDistance = 50;
  return Math.max(0, 1 - distance / maxDistance);
}

// 寻找相似患者
function findSimilarPatients(currentPreOp, trainingData) {
  if (!currentPreOp) return [];

  const similarities = trainingData.map((patient) => {
    if (!patient.data[0]) return { ...patient, similarity: 0 };

    const similarity = calculateSimilarity(currentPreOp, patient.data[0]);
    return { ...patient, similarity };
  });

  return similarities
    .filter((p) => p.similarity > 0.3)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 5);
}

// 从相似患者获取数据
function getDataFromSimilarPatients(targetMonth, similarPatients, currentData) {
  const validData = [];

  similarPatients.forEach((patient) => {
    if (patient.data[targetMonth]) {
      validData.push({
        data: patient.data[targetMonth],
        similarity: patient.similarity,
      });
    }
  });

  if (validData.length === 0) return null;

  let totalWeight = 0;
  const weightedSum = [0, 0, 0];

  validData.forEach((item) => {
    const weight = item.similarity;
    totalWeight += weight;
    for (let i = 0; i < 3; i++) {
      weightedSum[i] += item.data[i] * weight;
    }
  });

  const result = [
    Math.round(weightedSum[0] / totalWeight),
    Math.round(weightedSum[1] / totalWeight),
    Math.round(weightedSum[2] / totalWeight),
  ];

  return constrainToPreviousMonth(targetMonth, result, currentData);
}

// 约束数据不大于前一个时间点
function constrainToPreviousMonth(targetMonth, data, currentData) {
  const targetIndex = ALL_MONTHS.indexOf(targetMonth);

  if (targetIndex > 0) {
    const prevMonth = ALL_MONTHS[targetIndex - 1];
    if (currentData[prevMonth]) {
      for (let i = 0; i < 3; i++) {
        if (data[i] > currentData[prevMonth][i]) {
          data[i] = Math.max(1, Math.round(currentData[prevMonth][i] * 0.9));
        }
      }
    }
  }

  return data;
}
// 在两个数据点之间插值
function interpolateData(fromMonth, fromData, toMonth, toData, targetMonth) {
  const ratio = (targetMonth - fromMonth) / (toMonth - fromMonth);

  const result = fromData.map((val, index) => {
    const diff = toData[index] - val;
    return Math.round(val + diff * ratio);
  });

  return constrainToPreviousMonth(targetMonth, result, {
    [fromMonth]: fromData,
  });
}

// // 主函数：处理Excel数据
// function processExcelData(inputFilePath, outputFilePath) {
//   // 读取Excel文件
//   const workbook = XLSX.readFile(inputFilePath);
//   const worksheet = workbook.Sheets[workbook.SheetNames[0]];
//   const data = XLSX.utils.sheet_to_json(worksheet);

//   console.log(`开始处理 ${data.length} 名患者的数据...`);

//   const processedData = data.map((row, index) => {
//     console.log(`处理患者 ${index + 1}/${data.length}: ${row.姓名}`);

//     // 解析现有数据
//     const existingData = {};

//     // 硬化前 (月份0)
//     const preOpData = parseDimensionString(row.硬化前);
//     if (preOpData) {
//       existingData[0] = preOpData;
//     }

//     // 术后各月份数据
//     const monthMapping = {
//       微波后1月: 1,
//       "3月": 3,
//       "6月": 6,
//       "9月": 9,
//       "12月": 12,
//     };

//     Object.entries(monthMapping).forEach(([colName, month]) => {
//       const dimData = parseDimensionString(row[colName]);
//       if (dimData) {
//         existingData[month] = dimData;
//       }
//     });

//     // 必须要有术前数据才能模拟
//     if (!existingData[0]) {
//       console.warn(`患者 ${row.姓名} 缺少术前数据，跳过模拟`);
//       return { ...row, simulated: false };
//     }

//     // 寻找相似患者
//     const similarPatients = findSimilarPatients(
//       existingData[0],
//       DEFAULT_TRAINING_DATA
//     );

//     // 模拟缺失的数据
//     const resultData = { ...existingData };

//     // 按照时间顺序模拟缺失数据
//     const sortedMonths = [...ALL_MONTHS].sort((a, b) => a - b);
//     sortedMonths.forEach((month) => {
//       if (!resultData[month]) {
//         resultData[month] = simulateMonthData(
//           month,
//           resultData,
//           similarPatients,
//           DEFAULT_TRAINING_DATA
//         );
//       }
//     });

//     // 确保数据单调递减
//     ensureMonotonicDecrease(resultData);

//     // 构建结果行
//     const resultRow = { ...row };

//     // 更新各月份数据（保持原始数据不变，只填充空白）
//     if (!row["微波后1月"]) {
//       resultRow["微波后1月"] = formatDimensions(resultData[1]);
//     }
//     if (!row["3月"]) {
//       resultRow["3月"] = formatDimensions(resultData[3]);
//     }
//     if (!row["6月"]) {
//       resultRow["6月"] = formatDimensions(resultData[6]);
//     }
//     if (!row["9月"]) {
//       resultRow["9月"] = formatDimensions(resultData[9]);
//     }
//     if (!row["12月"]) {
//       resultRow["12月"] = formatDimensions(resultData[12]);
//     }

//     resultRow.simulated = true;
//     return resultRow;
//   });
//   // 创建新的工作表
//   const newWorksheet = XLSX.utils.json_to_sheet(processedData);

//   // 创建新的工作簿并保存
//   const newWorkbook = XLSX.utils.book_new();
//   XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, "补全数据");

//   // 保存文件
//   XLSX.writeFile(newWorkbook, outputFilePath);

//   console.log(`数据处理完成！结果已保存到: ${outputFilePath}`);

//   // 统计信息
//   const simulatedCount = processedData.filter((row) => row.simulated).length;
//   console.log(`成功模拟了 ${simulatedCount} 名患者的缺失数据`);
// }

// 使用示例
const inputFile = "硬化2020-2025.08（造影） - 副本_副本.xls";
const outputFile = "甲状腺结节数据_补全后.xlsx";

// 执行处理
// try {
//   processExcelData(inputFile, outputFile);
// } catch (error) {
//   console.error("处理过程中发生错误:", error);
// }
export default {
  name: "PatientDataSimulator",
  data() {
    // 初始化维度数据的辅助函数
    const initializeDimensions = () => {
      const dimensions = {};
      // 所有月份（0表示术前）
      const allMonths = [0, ...POST_MONTHS];
      allMonths.forEach((month) => {
        dimensions[month] = { length: null, width: null, height: null };
      });
      return dimensions;
    };

    // 从本地存储加载训练数据
    const loadTrainingData = () => {
      try {
        const savedData = localStorage.getItem(STORAGE_KEY);
        if (savedData) {
          return JSON.parse(savedData);
        }
      } catch (error) {
        console.error("加载训练数据失败:", error);
      }
      return DEFAULT_TRAINING_DATA;
    };

    return {
      // 术后观察月份
      postMonths: POST_MONTHS,
      allMonths: [0, ...POST_MONTHS],
      // 从本地存储加载训练数据
      trainingData: loadTrainingData(),
      newPatient: {
        name: "",
        dimensions: initializeDimensions(),
      },
      simulatedResult: null,
      similarPatients: [],
      trends: {},
      showDataManager: false,
    };
  },
  computed: {
    canSaveAsTrainingData() {
      // 检查是否可以保存为训练数据：至少需要有术前数据
      return (
        this.newPatient.name &&
        this.newPatient.dimensions[0].length &&
        this.newPatient.dimensions[0].width &&
        this.newPatient.dimensions[0].height
      );
    },
  },
  created() {
    // 在组件创建时计算趋势
    this.trends = this.calculateTrends();
  },
  methods: {
    triggerExcelImport() {
      this.$refs.excelFileInput.click();
    },

    // async handleExcelImport(event) {
    //   const file = event.target.files[0];
    //   if (!file) return;

    //   try {
    //     // 读取Excel文件
    //     const data = await this.readExcelFile(file);
    //     // 将Excel数据转换为患者列表
    //     const patients = this.convertExcelToPatients(data);
    //     // 对每个患者进行数据补全
    //     const completedPatients = patients.map((patient) =>
    //       this.simulatePatientData(patient)
    //     );
    //     // 将补全后的患者数据转换回Excel数据
    //     const excelData = this.convertPatientsToExcel(completedPatients);
    //     // 生成并下载Excel文件
    //     this.downloadExcel(excelData, "甲状腺结节数据_补全后.xlsx");
    //   } catch (error) {
    //     console.error("处理Excel文件时发生错误:", error);
    //     alert("处理Excel文件时发生错误，请查看控制台。");
    //   }

    //   event.target.value = ""; // 重置input
    // },
    // 修改 handleExcelImport 方法中的数据处理部分
    arraysEqual(arr1, arr2) {
      if (!arr1 || !arr2) return false;
      if (arr1.length !== arr2.length) return false;
      for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) return false;
      }
      return true;
    },
    // async handleExcelImport(event) {
    //   const file = event.target.files[0];
    //   if (!file) return;

    //   try {
    //     // 读取Excel文件
    //     const data = await this.readExcelFile(file);
    //     // 将Excel数据转换为患者列表
    //     const patients = this.convertExcelToPatients(data);

    //     // 对每个患者进行数据补全，但不覆盖原始数据
    //     const completedPatients = patients.map((patient) => {
    //       console.log(`处理患者: ${patient.name}`);
    //       // 记录原始数据
    //       const originalMonths = Object.keys(patient.data).map(Number);
    //       console.log(`原始数据月份: ${originalMonths}`);

    //       const completed = this.simulatePatientData(patient);

    //       // 验证没有覆盖原始数据
    //       originalMonths.forEach((month) => {
    //         if (!this.arraysEqual(patient.data[month], completed.data[month])) {
    //           console.warn(
    //             `警告: 患者 ${patient.name} 的 ${month} 月数据被修改!`
    //           );
    //           console.warn(
    //             `原始: ${patient.data[month]}, 现在: ${completed.data[month]}`
    //           );
    //         }
    //       });

    //       return completed;
    //     });

    //     // 将补全后的患者数据转换回Excel数据
    //     const excelData = this.convertPatientsToExcel(completedPatients);
    //     // 生成并下载Excel文件
    //     this.downloadExcel(excelData, "甲状腺结节数据_补全后_修正.xlsx");

    //     alert("数据处理完成！已下载修正后的文件。");
    //   } catch (error) {
    //     console.error("处理Excel文件时发生错误:", error);
    //     alert("处理Excel文件时发生错误，请查看控制台。");
    //   }

    //   event.target.value = ""; // 重置input
    // },
    //     async handleExcelImport(event) {
    //   const file = event.target.files[0];
    //   if (!file) return;

    //   try {
    //     // 读取Excel文件
    //     const data = await this.readExcelFile(file);
    //     // 将Excel数据转换为患者列表
    //     const patients = this.convertExcelToPatients(data);

    //     // 对每个患者进行数据补全
    //     const completedPatients = patients.map((patient) => {
    //       console.log(`处理患者: ${patient.name}`);
    //       // 记录原始数据
    //       const originalMonths = Object.keys(patient.data).map(Number);
    //       console.log(`原始数据月份: ${originalMonths}`);

    //       const completed = this.simulatePatientData(patient);

    //       // 验证没有覆盖原始数据
    //       originalMonths.forEach(month => {
    //         // 使用this.arraysEqual来比较数组
    //         if (!this.arraysEqual(patient.data[month], completed.data[month])) {
    //           console.warn(`警告: 患者 ${patient.name} 的 ${month} 月数据被修改!`);
    //           console.warn(`原始: ${patient.data[month]}, 现在: ${completed.data[month]}`);
    //         }
    //       });

    //       return completed;
    //     });

    //     // 将补全后的患者数据转换回Excel数据
    //     const excelData = this.convertPatientsToExcel(completedPatients);
    //     // 生成并下载Excel文件
    //     this.downloadExcel(excelData, "甲状腺结节数据_补全后_修正.xlsx");

    //     alert("数据处理完成！已下载修正后的文件。");
    //   } catch (error) {
    //     console.error("处理Excel文件时发生错误:", error);
    //     alert("处理Excel文件时发生错误，请查看控制台。");
    //   }

    //   event.target.value = ""; // 重置input
    // },
    // 修改 handleExcelImport 方法
    // async handleExcelImport(event) {
    //   const file = event.target.files[0];
    //   if (!file) return;

    //   try {
    //     // 读取Excel文件
    //     const data = await this.readExcelFile(file);
    //     // 将Excel数据转换为患者列表
    //     const patients = this.convertExcelToPatients(data);

    //     // 对每个患者进行数据补全，但不覆盖原始数据
    //     const completedPatients = patients.map((patient) => {
    //       console.log(`处理患者: ${patient.name}`);
    //       // 记录原始数据
    //       const originalData = { ...patient.data };

    //       const completed = this.simulatePatientData(patient);

    //       // 验证没有覆盖原始数据
    //       Object.keys(originalData).forEach((month) => {
    //         const original = originalData[month];
    //         const current = completed.data[month];
    //         if (!this.arraysEqual(original, current)) {
    //           console.warn(
    //             `警告: 患者 ${patient.name} 的 ${month} 月数据被修改!`
    //           );
    //           console.warn(`原始: ${original}, 现在: ${current}`);
    //           // 恢复原始数据
    //           completed.data[month] = original;
    //         }
    //       });

    //       return completed;
    //     });

    //     // 将补全后的患者数据转换回Excel数据
    //     const excelData = this.convertPatientsToExcel(completedPatients);
    //     // 生成并下载Excel文件
    //     this.downloadExcel(excelData, "甲状腺结节数据_补全后_修正.xlsx");

    //     alert("数据处理完成！已下载修正后的文件。");
    //   } catch (error) {
    //     console.error("处理Excel文件时发生错误:", error);
    //     alert("处理Excel文件时发生错误，请查看控制台。");
    //   }

    //   event.target.value = ""; // 重置input
    // },
    // 修改 handleExcelImport 方法，添加更严格的验证
    async handleExcelImport(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        // 读取Excel文件
        const data = await this.readExcelFile(file);
        // 将Excel数据转换为患者列表
        const patients = this.convertExcelToPatients(data);

        // 对每个患者进行数据补全，但不覆盖原始数据
        const completedPatients = patients.map((patient) => {
          console.log(`处理患者: ${patient.name}`);
          console.log(`原始数据:`, patient.originalData);

          const completed = this.simulatePatientData(patient);

          // 验证没有覆盖原始数据
          Object.keys(patient.originalData).forEach((month) => {
            const original = patient.originalData[month];
            const current = completed.data[month];
            if (!this.arraysEqual(original, current)) {
              console.error(
                `错误: 患者 ${patient.name} 的 ${month} 月数据被修改!`
              );
              console.error(`原始: ${original}, 现在: ${current}`);
              // 强制恢复原始数据
              completed.data[month] = [...original];
            }
          });

          return completed;
        });

        // 将补全后的患者数据转换回Excel数据
        const excelData = this.convertPatientsToExcel(completedPatients);
        // 生成并下载Excel文件
        this.downloadExcel(excelData, "甲状腺结节数据_补全后_修正.xlsx");

        alert("数据处理完成！已下载修正后的文件。");
      } catch (error) {
        console.error("处理Excel文件时发生错误:", error);
        alert("处理Excel文件时发生错误，请查看控制台。");
      }

      event.target.value = ""; // 重置input
    },
    readExcelFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },

    // 将Excel行数据转换为患者数据
    // convertExcelToPatients(excelData) {
    //   return excelData.map((row) => {
    //     const patient = {
    //       name: row["姓名"],
    //       data: {},
    //     };

    //     // 解析硬化前（0月）
    //     if (row["硬化前"]) {
    //       patient.data[0] = this.parseDimensionString(row["硬化前"]);
    //     }

    //     // 解析术后各月份
    //     const monthMapping = {
    //       微波后1月: 1,
    //       "3月": 3,
    //       "6月": 6,
    //       "9月": 9,
    //       "12月": 12,
    //     };

    //     Object.keys(monthMapping).forEach((key) => {
    //       if (row[key]) {
    //         patient.data[monthMapping[key]] = this.parseDimensionString(
    //           row[key]
    //         );
    //       }
    //     });

    //     return patient;
    //   });
    // },
    // convertExcelToPatients(excelData) {
    //   return excelData.map((row) => {
    //     const patient = {
    //       name: row["姓名"],
    //       data: {},
    //       originalData: {}, // 新增，记录原始字符串
    //     };

    //     // 记录每个月份的原始字符串
    //     patient.originalData[0] = row["硬化前"];

    //     const monthMapping = {
    //       微波后1月: 1,
    //       "3月": 3,
    //       "6月": 6,
    //       "9月": 9,
    //       "12月": 12,
    //     };

    //     Object.keys(monthMapping).forEach((key) => {
    //       patient.originalData[monthMapping[key]] = row[key];
    //     });

    //     // 解析硬化前（0月）
    //     if (row["硬化前"]) {
    //       patient.data[0] = this.parseDimensionString(row["硬化前"]);
    //     }

    //     // 解析术后各月份
    //     Object.keys(monthMapping).forEach((key) => {
    //       const month = monthMapping[key];
    //       if (row[key]) {
    //         patient.data[month] = this.parseDimensionString(row[key]);
    //       }
    //     });

    //     return patient;
    //   });
    // },
    // 修改 convertExcelToPatients 方法，确保正确解析原始数据
    convertExcelToPatients(excelData) {
      return excelData.map((row) => {
        const patient = {
          name: row["姓名"],
          data: {},
          // 添加一个字段来记录原始数据，确保不被覆盖
          originalData: {},
        };

        // 解析硬化前（0月）
        if (row["硬化前"]) {
          const parsed = this.parseDimensionString(row["硬化前"]);
          if (parsed) {
            patient.data[0] = parsed;
            patient.originalData[0] = parsed;
          }
        }

        // 解析术后各月份
        const monthMapping = {
          微波后1月: 1,
          "3月": 3,
          "6月": 6,
          "9月": 9,
          "12月": 12,
        };

        Object.keys(monthMapping).forEach((key) => {
          const month = monthMapping[key];
          if (row[key]) {
            const parsed = this.parseDimensionString(row[key]);
            if (parsed) {
              patient.data[month] = parsed;
              patient.originalData[month] = parsed;
            }
          }
        });

        return patient;
      });
    },
    // 解析尺寸字符串，例如 "53×44×32mm"
    parseDimensionString(str) {
      if (!str) return null;
      const match = str.match(/(\d+)×(\d+)×(\d+)mm/);
      if (match) {
        return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
      }
      return null;
    },

    // // 将患者数据转换回Excel行数据
    // convertPatientsToExcel(patients) {
    //   return patients.map((patient) => {
    //     const row = {
    //       姓名: patient.name,
    //       硬化前: this.formatDimensions(patient.data[0]),
    //       微波后1月: this.formatDimensions(patient.data[1]),
    //       "3月": this.formatDimensions(patient.data[3]),
    //       "6月": this.formatDimensions(patient.data[6]),
    //       "9月": this.formatDimensions(patient.data[9]),
    //       "12月": this.formatDimensions(patient.data[12]),
    //     };
    //     return row;
    //   });
    // },
    // 修改 convertPatientsToExcel 方法，确保不覆盖原始数据
    // convertPatientsToExcel(patients) {
    //   return patients.map((patient) => {
    //     const row = {
    //       姓名: patient.name,
    //       硬化前: this.formatDimensions(patient.data[0]),
    //     };

    //     // 对于每个术后月份，如果原始Excel中有数据就保留，没有才用模拟数据
    //     const monthMapping = {
    //       微波后1月: 1,
    //       "3月": 3,
    //       "6月": 6,
    //       "9月": 9,
    //       "12月": 12,
    //     };

    //     Object.keys(monthMapping).forEach((colName) => {
    //       const month = monthMapping[colName];
    //       // 这里应该检查原始Excel中该单元格是否为空
    //       // 但由于我们没有保存原始Excel数据，我们假设如果patient.data中有该月份数据就是原始数据
    //       row[colName] = this.formatDimensions(patient.data[month]);
    //     });

    //     return row;
    //   });
    // },
    // convertPatientsToExcel(patients) {
    //   return patients.map((patient) => {
    //     const row = {
    //       姓名: patient.name,
    //     };

    //     // 对于每个月份，如果原始数据存在，则使用原始字符串，否则使用模拟数据的格式化字符串
    //     const monthMapping = {
    //       硬化前: 0,
    //       微波后1月: 1,
    //       "3月": 3,
    //       "6月": 6,
    //       "9月": 9,
    //       "12月": 12,
    //     };
    // 修改 convertPatientsToExcel 方法，确保使用正确的数据
    convertPatientsToExcel(patients) {
      return patients.map((patient) => {
        const row = {
          姓名: patient.name,
          硬化前: this.formatDimensions(patient.data[0]),
          微波后1月: this.formatDimensions(patient.data[1]),
          "3月": this.formatDimensions(patient.data[3]),
          "6月": this.formatDimensions(patient.data[6]),
          "9月": this.formatDimensions(patient.data[9]),
          "12月": this.formatDimensions(patient.data[12]),
        };
        return row;
      });
    },
    //     Object.keys(monthMapping).forEach((colName) => {
    //       const month = monthMapping[colName];
    //       // 如果原始数据存在，则使用原始字符串
    //       if (
    //         patient.originalData[month] !== undefined &&
    //         patient.originalData[month] !== null &&
    //         patient.originalData[month] !== ""
    //       ) {
    //         row[colName] = patient.originalData[month];
    //       } else {
    //         // 否则使用模拟数据
    //         row[colName] = this.formatDimensions(patient.data[month]);
    //       }
    //     });

    //     return row;
    //   });
    // },
    // 格式化尺寸数组为字符串
    formatDimensions(dimensions) {
      if (!dimensions) return "";
      return `${dimensions[0]}×${dimensions[1]}×${dimensions[2]}mm`;
    },

    // 下载Excel文件
    downloadExcel(data, filename) {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },

    // // 模拟患者数据（补全缺失的月份）
    // simulatePatientData(patient) {
    //   // 如果患者没有术前数据，则无法模拟，直接返回
    //   if (!patient.data[0]) {
    //     return patient;
    //   }

    //   // 寻找相似患者
    //   const similarPatients = this.findSimilarPatients(patient.data);

    //   // 复制患者数据
    //   const resultData = { ...patient.data };

    //   // 按照时间顺序模拟缺失数据
    //   const sortedMonths = [0, 1, 3, 6, 9, 12];
    //   sortedMonths.forEach((month) => {
    //     if (!resultData[month]) {
    //       resultData[month] = this.simulateMonthData(
    //         month,
    //         resultData,
    //         similarPatients
    //       );
    //     }
    //   });

    //   // 确保数据单调递减
    //   this.ensureMonotonicDecrease(resultData);

    //   return {
    //     ...patient,
    //     data: resultData,
    //   };
    // },
    // 模拟患者数据（补全缺失的月份）
    // 修改 simulatePatientData 方法
    // simulatePatientData(patient) {
    //   // 如果患者没有术前数据，则无法模拟，直接返回
    //   if (!patient.data[0]) {
    //     return patient;
    //   }

    //   // 寻找相似患者
    //   const similarPatients = this.findSimilarPatients(patient.data);

    //   // 复制患者数据 - 重要：保留所有原始数据
    //   const resultData = { ...patient.data };

    //   // 只模拟缺失的数据，不覆盖已有数据
    //   const sortedMonths = [0, 1, 3, 6, 9, 12];
    //   sortedMonths.forEach((month) => {
    //     // 只有当该月份数据不存在时才进行模拟
    //     if (!resultData[month]) {
    //       console.log(`模拟患者 ${patient.name} 的 ${month} 月数据`);
    //       resultData[month] = this.simulateMonthData(
    //         month,
    //         resultData,
    //         similarPatients
    //       );
    //     } else {
    //       console.log(
    //         `保留患者 ${patient.name} 的 ${month} 月原始数据:`,
    //         resultData[month]
    //       );
    //     }
    //   });

    //   // 确保数据单调递减，但不要修改原始数据
    //   this.ensureMonotonicDecrease(resultData);

    //   return {
    //     ...patient,
    //     data: resultData,
    //   };
    // },
    // 修改 simulatePatientData 方法，确保不覆盖原始数据
    // simulatePatientData(patient) {
    //   // 如果患者没有术前数据，则无法模拟，直接返回
    //   if (!patient.data[0]) {
    //     return patient;
    //   }

    //   // 寻找相似患者
    //   const similarPatients = this.findSimilarPatients(patient.data);

    //   // 创建一个新的数据对象，首先复制所有原始数据
    //   const resultData = {};

    //   // 首先复制所有原始数据
    //   Object.keys(patient.originalData).forEach((month) => {
    //     resultData[month] = [...patient.originalData[month]]; // 创建副本
    //   });

    //   // 只模拟缺失的数据，不覆盖已有数据
    //   const sortedMonths = [0, 1, 3, 6, 9, 12];
    //   sortedMonths.forEach((month) => {
    //     // 只有当该月份数据不存在时才进行模拟
    //     if (!resultData[month]) {
    //       console.log(`模拟患者 ${patient.name} 的 ${month} 月数据`);
    //       resultData[month] = this.simulateMonthData(
    //         month,
    //         resultData,
    //         similarPatients
    //       );
    //     } else {
    //       console.log(
    //         `保留患者 ${patient.name} 的 ${month} 月原始数据:`,
    //         resultData[month]
    //       );
    //     }
    //   });

    //   // 确保数据单调递减，但不要修改原始数据
    //   this.ensureMonotonicDecrease(resultData);

    //   return {
    //     ...patient,
    //     data: resultData,
    //   };
    // },
    // 修改 simulatePatientData 方法，传递患者姓名
    simulatePatientData(patient) {
      // 如果患者没有术前数据，则无法模拟，直接返回
      if (!patient.data[0]) {
        return patient;
      }

      // 寻找相似患者
      const similarPatients = this.findSimilarPatients(patient.data);

      // 创建一个新的数据对象，首先复制所有原始数据
      const resultData = {};

      // 首先复制所有原始数据
      Object.keys(patient.originalData).forEach((month) => {
        resultData[month] = [...patient.originalData[month]]; // 创建副本
      });

      // 只模拟缺失的数据，不覆盖已有数据
      const sortedMonths = [0, 1, 3, 6, 9, 12];
      sortedMonths.forEach((month) => {
        // 只有当该月份数据不存在时才进行模拟
        if (!resultData[month]) {
          console.log(`模拟患者 ${patient.name} 的 ${month} 月数据`);
          resultData[month] = this.simulateMonthData(
            month,
            resultData,
            similarPatients,
            patient.name // 传递患者姓名用于特殊处理
          );
        } else {
          console.log(
            `保留患者 ${patient.name} 的 ${month} 月原始数据:`,
            resultData[month]
          );
        }
      });

      // 确保数据单调递减，但不要修改原始数据
      this.ensureMonotonicDecrease(resultData);

      return {
        ...patient,
        data: resultData,
      };
    },
    // 获取患者维度值
    getPatientDimensionValue(patient, month, dimension) {
      if (!patient.dimensions) {
        patient.dimensions = {};
      }
      if (!patient.dimensions[month]) {
        patient.dimensions[month] = { length: null, width: null, height: null };
      }
      return patient.dimensions[month][dimension];
    },

    // 更新患者维度值
    updatePatientDimension(patient, month, dimension, value) {
      if (!patient.dimensions) {
        patient.dimensions = {};
      }
      if (!patient.dimensions[month]) {
        patient.dimensions[month] = { length: null, width: null, height: null };
      }

      // 将输入值转换为数字，如果是空字符串则设为null
      const numValue = value === "" ? null : parseInt(value, 10);
      patient.dimensions[month][dimension] = isNaN(numValue) ? null : numValue;
    },
    // 获取患者维度数据的辅助方法
    getPatientDimension(patient, month, dimensionIndex) {
      if (!patient.data) {
        patient.data = {};
      }
      if (!patient.data[month]) {
        patient.data[month] = [null, null, null];
      }
      return patient.data[month][dimensionIndex];
    },

    // 保存训练数据到本地存储
    saveTrainingData() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.trainingData));
        alert("训练数据保存成功！");
      } catch (error) {
        console.error("保存训练数据失败:", error);
        alert("保存失败，请检查控制台获取详细信息。");
      }
    },

    // 重置训练数据
    resetTrainingData() {
      if (confirm("确定要重置训练数据吗？这将恢复为默认数据。")) {
        this.trainingData = [...DEFAULT_TRAINING_DATA];
        this.saveTrainingData();
      }
    },

    // 导出数据
    exportData() {
      const dataStr = JSON.stringify(this.trainingData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "thyroid_nodule_data.json";
      link.click();
      URL.revokeObjectURL(url);
    },

    // 导入数据
    importData() {
      this.$refs.fileInput.click();
    },

    // 处理文件导入
    handleFileImport(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          if (Array.isArray(importedData)) {
            this.trainingData = importedData;
            this.saveTrainingData();
            alert("数据导入成功！");
          } else {
            alert("导入的文件格式不正确。");
          }
        } catch (error) {
          console.error("导入数据失败:", error);
          alert("导入失败，请检查文件格式。");
        }
      };
      reader.readAsText(file);
      event.target.value = ""; // 重置文件输入
    },

    // 添加新患者
    addNewPatient() {
      this.trainingData.push({
        name: `患者${this.trainingData.length + 1}`,
        data: {},
      });
    },

    // 删除患者
    removePatient(index) {
      if (confirm("确定要删除这名患者的数据吗？")) {
        this.trainingData.splice(index, 1);
      }
    },

    // 将当前患者保存为训练数据
    saveAsTrainingData() {
      if (!this.canSaveAsTrainingData) return;

      // 构建患者数据对象
      const patientData = {
        name: this.newPatient.name,
        data: {},
      };

      // 添加所有有数据的月份
      this.allMonths.forEach((month) => {
        const dim = this.newPatient.dimensions[month];
        if (dim.length && dim.width && dim.height) {
          patientData.data[month] = [dim.length, dim.width, dim.height];
        }
      });

      // 添加到训练数据
      this.trainingData.push(patientData);
      this.saveTrainingData();
      alert("已保存为训练数据！");

      // 重新计算趋势
      this.trends = this.calculateTrends();
    },

    // 计算数据完整性
    calculateDataCompleteness() {
      let totalDataPoints = this.trainingData.length * this.allMonths.length;
      let filledDataPoints = 0;

      this.trainingData.forEach((patient) => {
        this.allMonths.forEach((month) => {
          if (patient.data && patient.data[month]) {
            filledDataPoints++;
          }
        });
      });

      return Math.round((filledDataPoints / totalDataPoints) * 100);
    },

    // 初始化维度数据
    initializeDimensions() {
      const dimensions = {};
      this.allMonths.forEach((month) => {
        dimensions[month] = { length: null, width: null, height: null };
      });
      return dimensions;
    },

    // 清空所有数据
    clearAllData() {
      this.newPatient = {
        name: "",
        dimensions: this.initializeDimensions(),
      };
      this.simulatedResult = null;
      this.similarPatients = [];
    },

    // 格式化维度显示
    // formatDimensions(dimensions) {
    //   return `${dimensions[0]} × ${dimensions[1]} × ${dimensions[2]} mm`;
    // },

    // 获取平均尺寸
    getAverageSize(month) {
      let sum = [0, 0, 0];
      let count = 0;

      this.trainingData.forEach((patient) => {
        if (patient.data[month]) {
          for (let i = 0; i < 3; i++) {
            sum[i] += patient.data[month][i];
          }
          count++;
        }
      });

      return count > 0
        ? sum.map((val) => Math.round(val / count))
        : [15, 12, 10];
    },

    // // 模拟缺失数据
    // simulateMissingData() {
    //   if (!this.newPatient.name) {
    //     alert("请输入患者姓名");
    //     return;
    //   }

    //   // 必须要有术前数据
    //   if (
    //     !this.newPatient.dimensions[0].length ||
    //     !this.newPatient.dimensions[0].width ||
    //     !this.newPatient.dimensions[0].height
    //   ) {
    //     alert("必须输入硬化前(术前)数据");
    //     return;
    //   }

    //   // 解析输入的数据
    //   const parsedData = {};

    //   this.allMonths.forEach((month) => {
    //     const dim = this.newPatient.dimensions[month];
    //     if (dim.length && dim.width && dim.height) {
    //       parsedData[month] = [dim.length, dim.width, dim.height];
    //     }
    //   });

    //   // 寻找相似患者
    //   this.similarPatients = this.findSimilarPatients(parsedData);

    //   // 模拟缺失的数据
    //   const result = {
    //     name: this.newPatient.name,
    //     data: { ...parsedData },
    //   };

    //   // 按照时间顺序模拟缺失数据，确保单调递减
    //   const sortedMonths = [...this.allMonths].sort((a, b) => a - b);
    //   sortedMonths.forEach((month) => {
    //     if (!result.data[month]) {
    //       result.data[month] = this.simulateMonthData(
    //         month,
    //         result.data,
    //         this.similarPatients
    //       );
    //     }
    //   });

    //   // 确保数据单调递减
    //   this.ensureMonotonicDecrease(result.data);

    //   this.simulatedResult = result;
    // },
    simulateMissingData() {
      if (!this.newPatient.name) {
        alert("请输入患者姓名");
        return;
      }

      // 必须要有术前数据
      if (
        !this.newPatient.dimensions[0].length ||
        !this.newPatient.dimensions[0].width ||
        !this.newPatient.dimensions[0].height
      ) {
        alert("必须输入硬化前(术前)数据");
        return;
      }

      // 构建患者数据对象
      const patientData = {
        name: this.newPatient.name,
        data: {},
      };

      this.allMonths.forEach((month) => {
        const dim = this.newPatient.dimensions[month];
        if (dim.length && dim.width && dim.height) {
          patientData.data[month] = [dim.length, dim.width, dim.height];
        }
      });

      // 使用simulatePatientData进行模拟
      const result = this.simulatePatientData(patientData);

      // 更新相似患者列表
      this.similarPatients = this.findSimilarPatients(patientData.data);

      this.simulatedResult = result;
    },
    // // 确保数据单调递减
    // ensureMonotonicDecrease(data) {
    //   for (let i = 1; i < this.allMonths.length; i++) {
    //     const currentMonth = this.allMonths[i];
    //     const prevMonth = this.allMonths[i - 1];

    //     if (data[currentMonth] && data[prevMonth]) {
    //       // 确保当前月份的数据不大于前一个月份的数据
    //       for (let j = 0; j < 3; j++) {
    //         if (data[currentMonth][j] > data[prevMonth][j]) {
    //           data[currentMonth][j] = Math.max(
    //             1,
    //             Math.round(data[prevMonth][j] * 0.9)
    //           );
    //         }
    //       }
    //     }
    //   }
    // },
    // 修改 ensureMonotonicDecrease 方法，使其更智能
    // ensureMonotonicDecrease(data) {
    //   for (let i = 1; i < this.allMonths.length; i++) {
    //     const currentMonth = this.allMonths[i];
    //     const prevMonth = this.allMonths[i - 1];

    //     if (data[currentMonth] && data[prevMonth]) {
    //       // 确保当前月份的数据不大于前一个月份的数据
    //       for (let j = 0; j < 3; j++) {
    //         if (data[currentMonth][j] > data[prevMonth][j]) {
    //           // 如果大于，则设置为前一个月份数据的90%
    //           data[currentMonth][j] = Math.max(
    //             1,
    //             Math.round(data[prevMonth][j] * 0.9)
    //           );
    //         }
    //       }
    //     }
    //   }

    //   // 额外检查：确保数据变化趋势合理
    //   for (let i = 1; i < this.allMonths.length - 1; i++) {
    //     const prevMonth = this.allMonths[i - 1];
    //     const currentMonth = this.allMonths[i];
    //     const nextMonth = this.allMonths[i + 1];

    //     if (data[prevMonth] && data[currentMonth] && data[nextMonth]) {
    //       for (let j = 0; j < 3; j++) {
    //         // 确保中间数据不会过小，导致后续数据不合理增大
    //         const prevToCurrentRatio =
    //           data[currentMonth][j] / data[prevMonth][j];
    //         const currentToNextRatio =
    //           data[nextMonth][j] / data[currentMonth][j];

    //         // 如果当前数据相对于前一个数据减少过多，但后续数据又增大，则调整当前数据
    //         if (prevToCurrentRatio < 0.7 && currentToNextRatio > 1.1) {
    //           data[currentMonth][j] = Math.round(
    //             (data[prevMonth][j] * 0.8 + data[nextMonth][j] * 1.2) / 2
    //           );
    //         }
    //       }
    //     }
    //   }
    // },
    // 修改 ensureMonotonicDecrease 方法，使其更加智能
    ensureMonotonicDecrease(data) {
      // 首先，确保数据在时间序列上是合理的
      for (let i = 1; i < this.allMonths.length; i++) {
        const currentMonth = this.allMonths[i];
        const prevMonth = this.allMonths[i - 1];

        if (data[currentMonth] && data[prevMonth]) {
          // 计算体积
          const currentVolume =
            data[currentMonth][0] *
            data[currentMonth][1] *
            data[currentMonth][2];
          const prevVolume =
            data[prevMonth][0] * data[prevMonth][1] * data[prevMonth][2];

          // 如果当前体积明显大于前一个体积，进行调整
          if (currentVolume > prevVolume * 1.1) {
            // 将当前数据调整为前一个数据的90%
            data[currentMonth] = [
              Math.max(1, Math.round(data[prevMonth][0] * 0.9)),
              Math.max(1, Math.round(data[prevMonth][1] * 0.9)),
              Math.max(1, Math.round(data[prevMonth][2] * 0.9)),
            ];
          }
        }
      }

      // 其次，检查中间数据是否过小
      for (let i = 1; i < this.allMonths.length - 1; i++) {
        const prevMonth = this.allMonths[i - 1];
        const currentMonth = this.allMonths[i];
        const nextMonth = this.allMonths[i + 1];

        if (data[prevMonth] && data[currentMonth] && data[nextMonth]) {
          // 计算体积
          const prevVolume =
            data[prevMonth][0] * data[prevMonth][1] * data[prevMonth][2];
          const currentVolume =
            data[currentMonth][0] *
            data[currentMonth][1] *
            data[currentMonth][2];
          const nextVolume =
            data[nextMonth][0] * data[nextMonth][1] * data[nextMonth][2];

          // 如果中间数据明显小于前后数据，进行调整
          if (
            currentVolume < prevVolume * 0.5 &&
            currentVolume < nextVolume * 0.5
          ) {
            // 将中间数据调整为前后数据的平均值
            data[currentMonth] = [
              Math.round((data[prevMonth][0] + data[nextMonth][0]) / 2),
              Math.round((data[prevMonth][1] + data[nextMonth][1]) / 2),
              Math.round((data[prevMonth][2] + data[nextMonth][2]) / 2),
            ];
          }
        }
      }
    },
    // 寻找相似患者
    findSimilarPatients(currentData) {
      const currentPreOp = currentData[0];
      if (!currentPreOp) return [];

      // 计算当前患者与其他患者的相似度
      const similarities = this.trainingData.map((patient) => {
        if (!patient.data[0]) return { ...patient, similarity: 0 };

        // 计算术前尺寸的相似度（使用欧几里得距离的倒数）
        const distance = Math.sqrt(
          Math.pow(patient.data[0][0] - currentPreOp[0], 2) +
            Math.pow(patient.data[0][1] - currentPreOp[1], 2) +
            Math.pow(patient.data[0][2] - currentPreOp[2], 2)
        );

        // 将距离转换为相似度（0-1范围）
        const maxDistance = 50; // 假设最大距离为50mm
        const similarity = Math.max(0, 1 - distance / maxDistance);

        return { ...patient, similarity };
      });

      // 按相似度排序并返回前5名
      return similarities
        .filter((p) => p.similarity > 0.3) // 过滤掉相似度过低的
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 5);
    },

    // // 模拟特定月份的数据
    // simulateMonthData(targetMonth, existingData, similarPatients) {
    //   // 首先尝试使用相似患者的数据
    //   if (similarPatients.length > 0) {
    //     const similarData = this.getDataFromSimilarPatients(
    //       targetMonth,
    //       similarPatients,
    //       existingData
    //     );
    //     if (similarData) {
    //       return similarData;
    //     }
    //   }

    //   // 如果没有相似患者数据，使用基于已知数据的插值
    //   return this.interpolateBasedOnKnownData(targetMonth, existingData);
    // },
    // 修改 simulateMonthData 方法，确保预测数据合理
    // simulateMonthData(targetMonth, existingData, similarPatients) {
    //   // 首先尝试使用相似患者的数据
    //   if (similarPatients.length > 0) {
    //     const similarData = this.getDataFromSimilarPatients(
    //       targetMonth,
    //       similarPatients,
    //       existingData
    //     );
    //     if (similarData) {
    //       // 确保预测数据与前后数据连贯
    //       return this.ensureDataConsistency(
    //         targetMonth,
    //         similarData,
    //         existingData
    //       );
    //     }
    //   }

    //   // 如果没有相似患者数据，使用基于已知数据的插值
    //   const interpolatedData = this.interpolateBasedOnKnownData(
    //     targetMonth,
    //     existingData
    //   );

    //   // 确保插值数据与前后数据连贯
    //   return this.ensureDataConsistency(
    //     targetMonth,
    //     interpolatedData,
    //     existingData
    //   );
    // },
    // simulateMonthData(targetMonth, existingData, similarPatients) {
    //   // 首先检查是否存在前后已知数据点，如果存在，优先使用线性插值
    //   const knownMonths = Object.keys(existingData)
    //     .map(Number)
    //     .sort((a, b) => a - b);
    //   let prevMonth = null;
    //   let nextMonth = null;

    //   for (let i = 0; i < knownMonths.length; i++) {
    //     if (knownMonths[i] < targetMonth) {
    //       prevMonth = knownMonths[i];
    //     }
    //     if (knownMonths[i] > targetMonth && !nextMonth) {
    //       nextMonth = knownMonths[i];
    //     }
    //   }

    //   // 如果存在前后已知数据点，使用线性插值
    //   if (prevMonth !== null && nextMonth !== null) {
    //     const interpolatedData = this.interpolateData(
    //       prevMonth,
    //       existingData[prevMonth],
    //       nextMonth,
    //       existingData[nextMonth],
    //       targetMonth
    //     );
    //     return this.ensureDataConsistency(
    //       targetMonth,
    //       interpolatedData,
    //       existingData
    //     );
    //   }

    //   // 其次尝试使用相似患者的数据
    //   if (similarPatients.length > 0) {
    //     const similarData = this.getDataFromSimilarPatients(
    //       targetMonth,
    //       similarPatients,
    //       existingData
    //     );
    //     if (similarData) {
    //       return this.ensureDataConsistency(
    //         targetMonth,
    //         similarData,
    //         existingData
    //       );
    //     }
    //   }

    //   // 最后使用基于已知数据的插值（这里实际上就是只有前一个或后一个点的情况）
    //   const interpolatedData = this.interpolateBasedOnKnownData(
    //     targetMonth,
    //     existingData
    //   );
    //   return this.ensureDataConsistency(
    //     targetMonth,
    //     interpolatedData,
    //     existingData
    //   );
    // },
    // 修改 simulateMonthData 方法，使用智能预测
    // simulateMonthData(targetMonth, existingData, similarPatients, patientName) {
    //   console.log(`为患者 ${patientName} 预测 ${targetMonth} 月数据`);

    //   // 使用基于体积变化率的智能预测
    //   return this.predictByVolumeReductionRate(
    //     targetMonth,
    //     existingData,
    //     similarPatients,
    //     patientName
    //   );
    // },
    // 完全重写预测逻辑
    simulateMonthData(targetMonth, existingData, similarPatients, patientName) {
      console.log(`为患者 ${patientName} 预测 ${targetMonth} 月数据`);

      // 使用基于时间序列的智能预测
      return this.intelligentTimeSeriesPrediction(
        targetMonth,
        existingData,
        similarPatients,
        patientName
      );
    },

    // 基于时间序列的智能预测
    intelligentTimeSeriesPrediction(
      targetMonth,
      existingData,
      similarPatients,
      patientName
    ) {
      // 获取所有已知月份的数据
      const knownMonths = Object.keys(existingData)
        .map(Number)
        .sort((a, b) => a - b);

      // 如果只有术前数据，使用基于相似患者的预测
      if (knownMonths.length === 1 && knownMonths[0] === 0) {
        return this.predictFromPreOpOnly(
          targetMonth,
          existingData[0],
          similarPatients
        );
      }

      // 找到目标月份前后的已知数据点
      let prevMonth = null;
      let nextMonth = null;

      for (let i = 0; i < knownMonths.length; i++) {
        if (knownMonths[i] < targetMonth) {
          prevMonth = knownMonths[i];
        }
        if (knownMonths[i] > targetMonth && !nextMonth) {
          nextMonth = knownMonths[i];
        }
      }

      // 根据不同情况采用不同的预测策略
      if (prevMonth !== null && nextMonth !== null) {
        // 情况1：前后都有已知数据 - 使用线性插值
        return this.linearInterpolationWithConstraints(
          prevMonth,
          existingData[prevMonth],
          nextMonth,
          existingData[nextMonth],
          targetMonth
        );
      } else if (prevMonth !== null) {
        // 情况2：只有前面有数据 - 使用趋势外推
        return this.trendExtrapolation(
          prevMonth,
          existingData[prevMonth],
          targetMonth,
          existingData
        );
      } else if (nextMonth !== null) {
        // 情况3：只有后面有数据 - 使用反向趋势外推
        return this.reverseTrendExtrapolation(
          nextMonth,
          existingData[nextMonth],
          targetMonth,
          existingData
        );
      } else {
        // 情况4：没有参考数据 - 使用基于相似患者的平均数据
        return this.getSimilarPatientAverage(
          targetMonth,
          similarPatients,
          existingData[0]
        );
      }
    },
    // 线性插值，但添加约束确保结果合理
    linearInterpolationWithConstraints(
      prevMonth,
      prevData,
      nextMonth,
      nextData,
      targetMonth
    ) {
      const ratio = (targetMonth - prevMonth) / (nextMonth - prevMonth);

      // 对每个维度进行线性插值
      const result = [
        Math.round(prevData[0] + (nextData[0] - prevData[0]) * ratio),
        Math.round(prevData[1] + (nextData[1] - prevData[1]) * ratio),
        Math.round(prevData[2] + (nextData[2] - prevData[2]) * ratio),
      ];

      // 确保结果在合理范围内
      return this.applyReasonableConstraints(result, prevData, nextData);
    },
    // 趋势外推
    trendExtrapolation(prevMonth, prevData, targetMonth, existingData) {
      const monthDiff = targetMonth - prevMonth;

      // 计算历史变化趋势
      const trend = this.calculateHistoricalTrend(existingData);

      // 基于趋势预测未来值
      let result;

      if (trend.available) {
        // 使用历史趋势
        const reductionRate = (trend.reductionRate * monthDiff) / 3; // 标准化到3个月
        result = [
          Math.max(1, Math.round(prevData[0] * (1 - reductionRate))),
          Math.max(1, Math.round(prevData[1] * (1 - reductionRate))),
          Math.max(1, Math.round(prevData[2] * (1 - reductionRate))),
        ];
      } else {
        // 使用默认趋势
        const defaultReduction = Math.min(0.3, 0.1 * monthDiff);
        result = [
          Math.max(1, Math.round(prevData[0] * (1 - defaultReduction))),
          Math.max(1, Math.round(prevData[1] * (1 - defaultReduction))),
          Math.max(1, Math.round(prevData[2] * (1 - defaultReduction))),
        ];
      }

      return this.applyReasonableConstraints(result, prevData, null);
    },

    // 计算历史变化趋势
    calculateHistoricalTrend(existingData) {
      const knownMonths = Object.keys(existingData)
        .map(Number)
        .sort((a, b) => a - b);

      if (knownMonths.length < 2) {
        return { available: false };
      }

      // 计算最近两个时间点的变化
      const lastMonth = knownMonths[knownMonths.length - 1];
      const prevMonth = knownMonths[knownMonths.length - 2];

      const lastVolume =
        existingData[lastMonth][0] *
        existingData[lastMonth][1] *
        existingData[lastMonth][2];
      const prevVolume =
        existingData[prevMonth][0] *
        existingData[prevMonth][1] *
        existingData[prevMonth][2];

      const reductionRate = (prevVolume - lastVolume) / prevVolume;
      const monthDiff = lastMonth - prevMonth;

      // 标准化到每月的减少率
      const monthlyReductionRate = reductionRate / monthDiff;

      return {
        available: true,
        reductionRate: monthlyReductionRate,
      };
    },
    // 基于相似患者的平均数据
    getSimilarPatientAverage(targetMonth, similarPatients, preOpData) {
      const validData = [];

      // 收集相似患者在目标月份的数据
      similarPatients.forEach((patient) => {
        if (patient.data[targetMonth]) {
          validData.push(patient.data[targetMonth]);
        }
      });

      if (validData.length === 0) {
        // 如果没有相似患者数据，使用基于术前数据的默认预测
        return this.getDefaultPrediction(targetMonth, preOpData);
      }

      // 计算平均值
      const sum = [0, 0, 0];
      validData.forEach((data) => {
        for (let i = 0; i < 3; i++) {
          sum[i] += data[i];
        }
      });

      const result = [
        Math.round(sum[0] / validData.length),
        Math.round(sum[1] / validData.length),
        Math.round(sum[2] / validData.length),
      ];

      return this.applyReasonableConstraints(result, null, null);
    },

    // 基于术前数据的默认预测
    getDefaultPrediction(targetMonth, preOpData) {
      // 基于术前数据和时间点计算默认预测
      const preOpVolume = preOpData[0] * preOpData[1] * preOpData[2];

      // 不同时间点的典型减少率
      const reductionRates = {
        1: 0.3, // 术后1月减少30%
        3: 0.5, // 术后3月减少50%
        6: 0.65, // 术后6月减少65%
        9: 0.75, // 术后9月减少75%
        12: 0.8, // 术后12月减少80%
      };

      const reductionRate = reductionRates[targetMonth] || 0.5;
      const volumeRatio = 1 - reductionRate;
      const linearRatio = Math.pow(volumeRatio, 1 / 3);

      const result = [
        Math.max(1, Math.round(preOpData[0] * linearRatio)),
        Math.max(1, Math.round(preOpData[1] * linearRatio)),
        Math.max(1, Math.round(preOpData[2] * linearRatio)),
      ];

      return this.applyReasonableConstraints(result, null, null);
    },

    // 只有术前数据的预测
    predictFromPreOpOnly(targetMonth, preOpData, similarPatients) {
      // 首先尝试使用相似患者的数据
      if (similarPatients.length > 0) {
        const similarData = this.getSimilarPatientAverage(
          targetMonth,
          similarPatients,
          preOpData
        );
        if (similarData) {
          return similarData;
        }
      }

      // 如果没有相似患者数据，使用基于术前数据的默认预测
      return this.getDefaultPrediction(targetMonth, preOpData);
    },

    // 反向趋势外推
    reverseTrendExtrapolation(nextMonth, nextData, targetMonth, existingData) {
      const monthDiff = nextMonth - targetMonth;

      // 基于后续数据反向预测
      const growthRate = Math.min(0.2, 0.05 * monthDiff);
      const result = [
        Math.round(nextData[0] * (1 + growthRate)),
        Math.round(nextData[1] * (1 + growthRate)),
        Math.round(nextData[2] * (1 + growthRate)),
      ];

      return this.applyReasonableConstraints(result, null, nextData);
    },

    // 应用合理约束
    applyReasonableConstraints(data, prevData, nextData) {
      const result = [...data];

      // 确保每个维度不会出现不合理的变化
      for (let i = 0; i < 3; i++) {
        // 如果前一个数据存在，确保不会明显大于前一个数据
        if (prevData && result[i] > prevData[i] * 1.15) {
          result[i] = Math.round(prevData[i] * 1.1);
        }

        // 如果后一个数据存在，确保不会明显小于后一个数据
        if (nextData && result[i] < nextData[i] * 0.85) {
          result[i] = Math.round(nextData[i] * 0.9);
        }

        // 确保不会出现极端值
        if (result[i] < 1) result[i] = 1;

        // 确保三个维度之间比例合理
        // 如果某个维度明显小于其他维度，适当调整
        const avg = (result[0] + result[1] + result[2]) / 3;
        if (result[i] < avg * 0.5) {
          result[i] = Math.round(avg * 0.7);
        }
      }

      return result;
    },

    // 基于体积变化率的智能预测
    predictByVolumeReductionRate(
      targetMonth,
      existingData,
      similarPatients,
      patientName
    ) {
      // 获取已知数据点
      const knownMonths = Object.keys(existingData)
        .map(Number)
        .sort((a, b) => a - b);

      // 计算已知数据的体积
      const knownVolumes = {};
      knownMonths.forEach((month) => {
        const dim = existingData[month];
        knownVolumes[month] = dim[0] * dim[1] * dim[2];
      });

      // 找到目标月份前后的已知数据点
      let prevMonth = null;
      let nextMonth = null;

      for (let i = 0; i < knownMonths.length; i++) {
        if (knownMonths[i] < targetMonth) {
          prevMonth = knownMonths[i];
        }
        if (knownMonths[i] > targetMonth && !nextMonth) {
          nextMonth = knownMonths[i];
        }
      }

      // 根据不同情况采用不同的预测策略
      if (prevMonth !== null && nextMonth !== null) {
        // 情况1：前后都有已知数据 - 使用智能插值
        return this.smartInterpolation(
          prevMonth,
          existingData[prevMonth],
          nextMonth,
          existingData[nextMonth],
          targetMonth,
          existingData
        );
      } else if (prevMonth !== null) {
        // 情况2：只有前面有数据 - 使用基于相似患者的趋势预测
        return this.predictFromPreviousWithSimilarPatients(
          prevMonth,
          existingData[prevMonth],
          targetMonth,
          similarPatients,
          existingData
        );
      } else if (nextMonth !== null) {
        // 情况3：只有后面有数据 - 使用反向预测
        return this.predictFromNextWithSimilarPatients(
          nextMonth,
          existingData[nextMonth],
          targetMonth,
          similarPatients,
          existingData
        );
      } else {
        // 情况4：没有参考数据 - 使用平均数据
        return this.getAverageData(targetMonth);
      }
    },
    // 智能插值 - 考虑体积变化趋势
    smartInterpolation(
      prevMonth,
      prevData,
      nextMonth,
      nextData,
      targetMonth,
      existingData
    ) {
      const prevVolume = prevData[0] * prevData[1] * prevData[2];
      const nextVolume = nextData[0] * nextData[1] * nextData[2];

      // 计算体积减少率
      const volumeReduction = (prevVolume - nextVolume) / prevVolume;
      const totalMonths = nextMonth - prevMonth;
      const elapsedMonths = targetMonth - prevMonth;

      // 微波治疗后的体积变化通常遵循指数衰减模式
      // 初期减少快，后期减少慢
      const decayFactor = 0.7; // 调整这个参数可以控制衰减速度

      // 计算目标月份的理论体积
      const progressRatio = elapsedMonths / totalMonths;
      const effectiveProgress = 1 - Math.pow(1 - progressRatio, decayFactor);
      const targetVolume =
        prevVolume * (1 - volumeReduction * effectiveProgress);

      // 基于原始比例计算各维度
      const volumeRatio = targetVolume / prevVolume;
      const linearRatio = Math.pow(volumeRatio, 1 / 3); // 体积的立方根对应线性尺寸

      const result = [
        Math.max(1, Math.round(prevData[0] * linearRatio)),
        Math.max(1, Math.round(prevData[1] * linearRatio)),
        Math.max(1, Math.round(prevData[2] * linearRatio)),
      ];

      // 确保结果合理
      return this.ensureReasonableSize(
        result,
        prevData,
        nextData,
        targetMonth,
        existingData
      );
    },
    // 基于相似患者的趋势预测
    predictFromPreviousWithSimilarPatients(
      prevMonth,
      prevData,
      targetMonth,
      similarPatients,
      existingData
    ) {
      // 首先尝试从相似患者获取数据
      if (similarPatients.length > 0) {
        const similarData = this.getRealisticDataFromSimilarPatients(
          targetMonth,
          similarPatients,
          existingData
        );
        if (similarData) {
          return similarData;
        }
      }

      // 如果没有合适的相似患者数据，使用基于训练数据的趋势预测
      const monthDiff = targetMonth - prevMonth;

      // 计算训练数据中类似情况下的平均减少率
      const avgReduction = this.calculateRealisticReductionRate(
        prevMonth,
        targetMonth,
        prevData
      );

      // 应用减少率，考虑非线性衰减
      const reductionFactor = this.getReductionFactor(
        prevMonth,
        targetMonth,
        monthDiff
      );

      const result = prevData.map((val) =>
        Math.max(1, Math.round(val * reductionFactor))
      );

      return this.ensureReasonableSize(
        result,
        prevData,
        null,
        targetMonth,
        existingData
      );
    },
    // 从相似患者获取现实数据
    getRealisticDataFromSimilarPatients(
      targetMonth,
      similarPatients,
      currentData
    ) {
      const validData = [];

      // 只考虑那些有合理数据的相似患者
      similarPatients.forEach((patient) => {
        if (patient.data[targetMonth]) {
          // 检查这个数据是否合理（不会导致尺寸突然增大）
          if (
            this.isDataReasonable(
              targetMonth,
              patient.data[targetMonth],
              currentData
            )
          ) {
            validData.push({
              data: patient.data[targetMonth],
              similarity: patient.similarity,
            });
          }
        }
      });

      if (validData.length === 0) return null;

      // 使用加权平均
      let totalWeight = 0;
      const weightedSum = [0, 0, 0];

      validData.forEach((item) => {
        const weight = item.similarity;
        totalWeight += weight;
        for (let i = 0; i < 3; i++) {
          weightedSum[i] += item.data[i] * weight;
        }
      });

      const result = [
        Math.round(weightedSum[0] / totalWeight),
        Math.round(weightedSum[1] / totalWeight),
        Math.round(weightedSum[2] / totalWeight),
      ];

      return this.ensureReasonableSize(
        result,
        currentData[0],
        null,
        targetMonth,
        currentData
      );
    },
    // 获取减少因子
    getReductionFactor(fromMonth, toMonth, monthDiff) {
      // 微波治疗后初期减少快，后期减少慢
      if (fromMonth === 0) {
        // 术前到术后：大幅减少
        return 1 - 0.4 * Math.min(monthDiff / 3, 1);
      } else if (monthDiff <= 3) {
        // 短期减少：中等减少
        return 1 - 0.2 * (monthDiff / 3);
      } else {
        // 长期减少：缓慢减少
        return 1 - 0.1 * Math.min(monthDiff / 6, 1);
      }
    },
    // 计算现实的减少率
    calculateRealisticReductionRate(fromMonth, toMonth, fromData) {
      const fromVolume = fromData[0] * fromData[1] * fromData[2];
      let totalReduction = 0;
      let count = 0;

      // 只考虑术前尺寸相似的患者
      this.trainingData.forEach((patient) => {
        if (patient.data[fromMonth] && patient.data[toMonth]) {
          const patientFromVolume =
            patient.data[fromMonth][0] *
            patient.data[fromMonth][1] *
            patient.data[fromMonth][2];

          // 只考虑术前体积相似的患者（±30%范围内）
          if (Math.abs(patientFromVolume - fromVolume) / fromVolume < 0.3) {
            const patientToVolume =
              patient.data[toMonth][0] *
              patient.data[toMonth][1] *
              patient.data[toMonth][2];
            totalReduction +=
              (patientFromVolume - patientToVolume) / patientFromVolume;
            count++;
          }
        }
      });

      // 如果有足够的数据，使用平均值；否则使用默认值
      if (count > 3) {
        return totalReduction / count;
      } else {
        // 基于时间间隔的默认减少率
        const monthDiff = toMonth - fromMonth;
        return Math.min(0.5, 0.1 * monthDiff);
      }
    },
    // 反向预测（从后往前）
    predictFromNextWithSimilarPatients(
      nextMonth,
      nextData,
      targetMonth,
      similarPatients,
      existingData
    ) {
      const monthDiff = nextMonth - targetMonth;

      // 计算反向增长率
      const growthFactor = 1 + 0.15 * Math.min(monthDiff / 3, 1);

      const result = nextData.map((val) => Math.round(val * growthFactor));

      return this.ensureReasonableSize(
        result,
        null,
        nextData,
        targetMonth,
        existingData
      );
    },
    // 确保尺寸合理
    ensureReasonableSize(data, prevData, nextData, targetMonth, existingData) {
      const result = [...data];
      const targetIndex = this.allMonths.indexOf(targetMonth);

      // 检查前一个时间点
      if (targetIndex > 0 && prevData) {
        const prevMonth = this.allMonths[targetIndex - 1];
        for (let i = 0; i < 3; i++) {
          // 确保不会明显大于前一个时间点
          if (result[i] > prevData[i] * 1.1) {
            result[i] = Math.max(1, Math.round(prevData[i] * 1.05));
          }
        }
      }

      // 检查后一个时间点
      if (targetIndex < this.allMonths.length - 1 && nextData) {
        const nextMonth = this.allMonths[targetIndex + 1];
        for (let i = 0; i < 3; i++) {
          // 确保不会明显小于后一个时间点
          if (result[i] < nextData[i] * 0.9) {
            result[i] = Math.round(nextData[i] * 0.95);
          }
        }
      }

      // 确保不会出现极端值
      for (let i = 0; i < 3; i++) {
        if (result[i] < 1) result[i] = 1;
        if (result[i] > 100) result[i] = 100;
      }

      return result;
    },
    // 检查数据是否合理
    isDataReasonable(targetMonth, candidateData, currentData) {
      const targetIndex = this.allMonths.indexOf(targetMonth);

      // 检查前一个时间点
      if (targetIndex > 0) {
        const prevMonth = this.allMonths[targetIndex - 1];
        if (currentData[prevMonth]) {
          for (let i = 0; i < 3; i++) {
            // 如果候选数据明显大于前一个时间点，则不合理
            if (candidateData[i] > currentData[prevMonth][i] * 1.2) {
              return false;
            }
          }
        }
      }

      // 检查后一个时间点
      if (targetIndex < this.allMonths.length - 1) {
        const nextMonth = this.allMonths[targetIndex + 1];
        if (currentData[nextMonth]) {
          for (let i = 0; i < 3; i++) {
            // 如果候选数据明显小于后一个时间点，则不合理
            if (candidateData[i] < currentData[nextMonth][i] * 0.8) {
              return false;
            }
          }
        }
      }

      return true;
    },
    // 添加确保数据一致性的方法
    ensureDataConsistency(targetMonth, data, existingData) {
      const targetIndex = this.allMonths.indexOf(targetMonth);
      const result = [...data]; // 创建副本

      // 检查前一个时间点的数据
      if (targetIndex > 0) {
        const prevMonth = this.allMonths[targetIndex - 1];
        if (existingData[prevMonth]) {
          // 确保当前数据不大于前一个时间点的数据
          for (let i = 0; i < 3; i++) {
            if (result[i] > existingData[prevMonth][i]) {
              // 如果大于，则设置为前一个时间点的90%
              result[i] = Math.max(
                1,
                Math.round(existingData[prevMonth][i] * 0.9)
              );
            }
          }
        }
      }

      // 检查后一个时间点的数据
      if (targetIndex < this.allMonths.length - 1) {
        const nextMonth = this.allMonths[targetIndex + 1];
        if (existingData[nextMonth]) {
          // 确保当前数据不小于后一个时间点的数据
          for (let i = 0; i < 3; i++) {
            if (result[i] < existingData[nextMonth][i]) {
              // 如果小于，则设置为后一个时间点的110%
              result[i] = Math.round(existingData[nextMonth][i] * 1.1);
            }
          }
        }
      }

      return result;
    },

    // 修改 interpolateBasedOnKnownData 方法，使其更智能
    interpolateBasedOnKnownData(targetMonth, existingData) {
      // 找到最接近的已知数据点
      const knownMonths = Object.keys(existingData)
        .map(Number)
        .sort((a, b) => a - b);

      if (knownMonths.length === 0) {
        return this.getAverageData(targetMonth);
      }

      // 找到前一个和后一个已知月份
      let prevMonth = null;
      let nextMonth = null;

      for (let i = 0; i < knownMonths.length; i++) {
        if (knownMonths[i] < targetMonth) {
          prevMonth = knownMonths[i];
        }
        if (knownMonths[i] > targetMonth && !nextMonth) {
          nextMonth = knownMonths[i];
        }
      }

      if (prevMonth !== null && nextMonth !== null) {
        // 在两个已知点之间插值
        return this.interpolateData(
          prevMonth,
          existingData[prevMonth],
          nextMonth,
          existingData[nextMonth],
          targetMonth
        );
      } else if (prevMonth !== null) {
        // 只有前一个点，使用基于相似患者的趋势预测
        return this.predictFromPrevious(
          prevMonth,
          existingData[prevMonth],
          targetMonth
        );
      } else {
        // 只有后一个点，使用基于相似患者的趋势反向预测
        return this.predictFromNext(
          nextMonth,
          existingData[nextMonth],
          targetMonth
        );
      }
    },

    // // 从相似患者获取数据
    // getDataFromSimilarPatients(targetMonth, similarPatients, currentData) {
    //   // 收集所有相似患者在目标月份的数据
    //   const validData = [];

    //   similarPatients.forEach((patient) => {
    //     if (patient.data[targetMonth]) {
    //       validData.push({
    //         data: patient.data[targetMonth],
    //         similarity: patient.similarity,
    //       });
    //     }
    //   });

    //   if (validData.length === 0) return null;

    //   // 使用加权平均，相似度高的患者权重更高
    //   let totalWeight = 0;
    //   const weightedSum = [0, 0, 0];

    //   validData.forEach((item) => {
    //     const weight = item.similarity;
    //     totalWeight += weight;
    //     for (let i = 0; i < 3; i++) {
    //       weightedSum[i] += item.data[i] * weight;
    //     }
    //   });

    //   // 计算加权平均值
    //   const result = [
    //     Math.round(weightedSum[0] / totalWeight),
    //     Math.round(weightedSum[1] / totalWeight),
    //     Math.round(weightedSum[2] / totalWeight),
    //   ];

    //   // 确保结果不会大于前一个时间点的数据
    //   return this.constrainToPreviousMonth(targetMonth, result, currentData);
    // },
    // 修改 getDataFromSimilarPatients 方法，使其更智能
    getDataFromSimilarPatients(targetMonth, similarPatients, currentData) {
      // 收集所有相似患者在目标月份的数据
      const validData = [];

      similarPatients.forEach((patient) => {
        if (patient.data[targetMonth]) {
          validData.push({
            data: patient.data[targetMonth],
            similarity: patient.similarity,
          });
        }
      });

      if (validData.length === 0) return null;

      // 使用加权平均，相似度高的患者权重更高
      let totalWeight = 0;
      const weightedSum = [0, 0, 0];

      validData.forEach((item) => {
        const weight = item.similarity;
        totalWeight += weight;
        for (let i = 0; i < 3; i++) {
          weightedSum[i] += item.data[i] * weight;
        }
      });

      // 计算加权平均值
      const result = [
        Math.round(weightedSum[0] / totalWeight),
        Math.round(weightedSum[1] / totalWeight),
        Math.round(weightedSum[2] / totalWeight),
      ];

      // 确保结果与前后数据连贯
      return this.ensureDataConsistency(targetMonth, result, currentData);
    },

    // 约束数据不大于前一个时间点
    constrainToPreviousMonth(targetMonth, data, currentData) {
      const targetIndex = this.allMonths.indexOf(targetMonth);

      if (targetIndex > 0) {
        const prevMonth = this.allMonths[targetIndex - 1];
        if (currentData[prevMonth]) {
          for (let i = 0; i < 3; i++) {
            if (data[i] > currentData[prevMonth][i]) {
              data[i] = Math.max(
                1,
                Math.round(currentData[prevMonth][i] * 0.9)
              );
            }
          }
        }
      }

      return data;
    },
    // 计算训练数据的趋势
    calculateTrends() {
      const trends = {};

      // 计算每个时间段的变化趋势
      for (let i = 0; i < this.allMonths.length - 1; i++) {
        const fromMonth = this.allMonths[i];
        const toMonth = this.allMonths[i + 1];
        const monthDiff = toMonth - fromMonth;

        let totalReduction = [0, 0, 0];
        let count = 0;

        this.trainingData.forEach((patient) => {
          if (patient.data[fromMonth] && patient.data[toMonth]) {
            for (let j = 0; j < 3; j++) {
              const fromVal = patient.data[fromMonth][j];
              const toVal = patient.data[toMonth][j];
              // 计算减少比例
              totalReduction[j] += (fromVal - toVal) / fromVal;
            }
            count++;
          }
        });

        const avgReduction =
          count > 0
            ? totalReduction.map((val) => val / count)
            : this.getDefaultReduction(fromMonth, toMonth, monthDiff);

        trends[`${fromMonth}-${toMonth}`] = {
          duration: monthDiff,
          avgReduction: avgReduction,
        };
      }

      return trends;
    },

    // 获取默认减少率
    getDefaultReduction(fromMonth, toMonth, monthDiff) {
      // 微波治疗后初期减少较快，后期减缓
      if (fromMonth === 0) {
        // 术前到术后1月：大幅减少
        return [0.4, 0.4, 0.4];
      } else if (fromMonth === 1) {
        // 1月到3月：中等减少
        return [0.3, 0.3, 0.3];
      } else {
        // 后期：缓慢减少，考虑时间间隔因素
        const baseRate = 0.15;
        // 根据时间间隔调整减少率，时间越长减少率越高
        const adjustedRate = Math.min(0.3, baseRate * (monthDiff / 2));
        return [adjustedRate, adjustedRate, adjustedRate];
      }
    },

    // 获取平均数据
    getAverageData(month) {
      let sum = [0, 0, 0];
      let count = 0;

      this.trainingData.forEach((patient) => {
        if (patient.data[month]) {
          for (let i = 0; i < 3; i++) {
            sum[i] += patient.data[month][i];
          }
          count++;
        }
      });

      return count > 0
        ? sum.map((val) => Math.round(val / count))
        : this.getDefaultSize(month);
    },

    // 获取默认尺寸
    getDefaultSize(month) {
      // 基于训练数据的典型尺寸
      const defaultSizes = {
        0: [40, 32, 25], // 术前平均
        1: [25, 20, 15], // 术后1月
        3: [18, 15, 11], // 术后3月
        6: [15, 12, 9], // 术后6月
        9: [12, 10, 8], // 术后9月
        12: [10, 8, 6], // 术后12月
      };
      return defaultSizes[month] || [15, 12, 10];
    },

    // // 在两个数据点之间插值
    // interpolateData(fromMonth, fromData, toMonth, toData, targetMonth) {
    //   const ratio = (targetMonth - fromMonth) / (toMonth - fromMonth);

    //   const result = fromData.map((val, index) => {
    //     const diff = toData[index] - val;
    //     return Math.round(val + diff * ratio);
    //   });

    //   // 确保插值结果不会大于前一个时间点
    //   return this.constrainToPreviousMonth(targetMonth, result, {
    //     [fromMonth]: fromData,
    //   });
    // },
    interpolateData(fromMonth, fromData, toMonth, toData, targetMonth) {
      const ratio = (targetMonth - fromMonth) / (toMonth - fromMonth);

      const result = fromData.map((val, index) => {
        const diff = toData[index] - val;
        return Math.round(val + diff * ratio);
        //   // 确保插值结果不会大于前一个时间点
        //   return this.constrainToPreviousMonth(targetMonth, result, {
        //     [fromMonth]: fromData,
        //   });
      });

      return result;
    },
    // // 从前一个点预测
    // predictFromPrevious(fromMonth, fromData, targetMonth) {
    //   const monthDiff = targetMonth - fromMonth;

    //   // 基于真实数据计算减少率
    //   let totalReduction = [0, 0, 0];
    //   let count = 0;

    //   this.trainingData.forEach((patient) => {
    //     if (patient.data[fromMonth] && patient.data[targetMonth]) {
    //       for (let i = 0; i < 3; i++) {
    //         const fromVal = patient.data[fromMonth][i];
    //         const toVal = patient.data[targetMonth][i];
    //         totalReduction[i] += (fromVal - toVal) / fromVal;
    //       }
    //       count++;
    //     }
    //   });

    //   let result;

    //   if (count > 0) {
    //     // 使用真实数据的平均减少率
    //     const avgReduction = totalReduction.map((val) => val / count);
    //     result = fromData.map((val, index) => {
    //       const reduction = val * avgReduction[index];
    //       return Math.max(1, Math.round(val - reduction));
    //     });
    //   } else {
    //     // 如果没有真实数据，使用基于时间间隔的默认减少率
    //     const defaultReduction = Math.min(0.5, 0.2 * monthDiff);
    //     result = fromData.map((val) =>
    //       Math.max(1, Math.round(val * (1 - defaultReduction)))
    //     );
    //   }

    //   // 确保预测结果不会大于前一个时间点
    //   return this.constrainToPreviousMonth(targetMonth, result, {
    //     [fromMonth]: fromData,
    //   });
    // },

    // // 从后一个点反向预测
    // predictFromNext(toMonth, toData, targetMonth) {
    //   const monthDiff = toMonth - targetMonth;

    //   // 基于真实数据计算增长率
    //   let totalGrowth = [0, 0, 0];
    //   let count = 0;

    //   this.trainingData.forEach((patient) => {
    //     if (patient.data[targetMonth] && patient.data[toMonth]) {
    //       for (let i = 0; i < 3; i++) {
    //         const fromVal = patient.data[targetMonth][i];
    //         const toVal = patient.data[toMonth][i];
    //         totalGrowth[i] += (toVal - fromVal) / fromVal;
    //       }
    //       count++;
    //     }
    //   });

    //   let result;

    //   if (count > 0) {
    //     // 使用真实数据的平均增长率
    //     const avgGrowth = totalGrowth.map((val) => val / count);
    //     result = toData.map((val, index) => {
    //       const growth = val * avgGrowth[index];
    //       return Math.round(val + growth);
    //     });
    //   } else {
    //     // 如果没有真实数据，使用基于时间间隔的默认增长率
    //     const defaultGrowth = Math.min(0.5, 0.15 * monthDiff);
    //     result = toData.map((val) => Math.round(val * (1 + defaultGrowth)));
    //   }

    //   // 确保反向预测结果不会小于后一个时间点
    //   for (let i = 0; i < 3; i++) {
    //     if (result[i] < toData[i]) {
    //       result[i] = Math.round(toData[i] * 1.1); // 稍微大于后一个时间点
    //     }
    //   }

    //   return result;
    // },
    // 修改 predictFromPrevious 方法，使用更合理的预测逻辑
    predictFromPrevious(fromMonth, fromData, targetMonth) {
      const monthDiff = targetMonth - fromMonth;

      // 基于训练数据计算平均减少率
      const avgReduction = this.calculateAverageReduction(
        fromMonth,
        targetMonth
      );

      // 应用减少率，但考虑非线性减少（初期减少快，后期减少慢）
      let reductionFactor;
      if (fromMonth === 0) {
        // 从术前到术后：大幅减少
        reductionFactor = 0.6; // 减少40%
      } else if (monthDiff <= 3) {
        // 短期减少：中等减少
        reductionFactor = 0.8; // 减少20%
      } else {
        // 长期减少：缓慢减少
        reductionFactor = 0.9; // 减少10%
      }

      // 应用减少率，考虑时间间隔
      const timeAdjustedReduction =
        1 - (1 - reductionFactor) * Math.min(monthDiff / 3, 1);

      const result = fromData.map((val) =>
        Math.max(1, Math.round(val * timeAdjustedReduction))
      );

      return result;
    },

    // 修改 predictFromNext 方法，使用更合理的预测逻辑
    predictFromNext(toMonth, toData, targetMonth) {
      const monthDiff = toMonth - targetMonth;

      // 基于训练数据计算平均增长率
      const avgGrowth = this.calculateAverageGrowth(targetMonth, toMonth);

      // 应用增长率，但考虑非线性增长（离目标点越近增长越小）
      let growthFactor;
      if (toMonth === 1) {
        // 反向预测到术前：大幅增长
        growthFactor = 1.4; // 增长40%
      } else if (monthDiff <= 3) {
        // 短期增长：中等增长
        growthFactor = 1.2; // 增长20%
      } else {
        // 长期增长：缓慢增长
        growthFactor = 1.1; // 增长10%
      }

      // 应用增长率，考虑时间间隔
      const timeAdjustedGrowth =
        1 + (growthFactor - 1) * Math.min(monthDiff / 3, 1);

      const result = toData.map((val) => Math.round(val * timeAdjustedGrowth));

      return result;
    },
    // 添加计算平均减少率的方法
    calculateAverageReduction(fromMonth, toMonth) {
      let totalReduction = [0, 0, 0];
      let count = 0;

      this.trainingData.forEach((patient) => {
        if (patient.data[fromMonth] && patient.data[toMonth]) {
          for (let i = 0; i < 3; i++) {
            const fromVal = patient.data[fromMonth][i];
            const toVal = patient.data[toMonth][i];
            if (fromVal > 0) {
              totalReduction[i] += (fromVal - toVal) / fromVal;
            }
          }
          count++;
        }
      });

      return count > 0
        ? totalReduction.map((val) => val / count)
        : [0.3, 0.3, 0.3];
    },

    // 添加计算平均增长率的方法
    calculateAverageGrowth(fromMonth, toMonth) {
      let totalGrowth = [0, 0, 0];
      let count = 0;

      this.trainingData.forEach((patient) => {
        if (patient.data[fromMonth] && patient.data[toMonth]) {
          for (let i = 0; i < 3; i++) {
            const fromVal = patient.data[fromMonth][i];
            const toVal = patient.data[toMonth][i];
            if (fromVal > 0) {
              totalGrowth[i] += (toVal - fromVal) / fromVal;
            }
          }
          count++;
        }
      });

      return count > 0
        ? totalGrowth.map((val) => val / count)
        : [0.2, 0.2, 0.2];
    },

    // 检查是否有原始数据
    hasOriginalData(month) {
      if (!this.simulatedResult) return false;
      const dim = this.newPatient.dimensions[month];
      return !!(dim && dim.length && dim.width && dim.height);
    },

    // 计算柱状图高度
    calculateBarHeight(size) {
      // 基于体积计算相对高度
      const volume = size[0] * size[1] * size[2];
      const maxVolume = 60 * 50 * 40; // 假设最大体积
      return Math.max(5, (volume / maxVolume) * 100);
    },

    // 计算体积减少率
    calculateVolumeReduction() {
      if (!this.simulatedResult) return 0;
      const preOpVolume =
        this.simulatedResult.data[0][0] *
        this.simulatedResult.data[0][1] *
        this.simulatedResult.data[0][2];
      const postOpVolume =
        this.simulatedResult.data[12][0] *
        this.simulatedResult.data[12][1] *
        this.simulatedResult.data[12][2];
      return Math.round(((preOpVolume - postOpVolume) / preOpVolume) * 100);
    },

    // 计算最大尺寸减少率
    calculateMaxDimensionReduction() {
      if (!this.simulatedResult) return 0;
      const preOpMax = Math.max(...this.simulatedResult.data[0]);
      const postOpMax = Math.max(...this.simulatedResult.data[12]);
      return Math.round(((preOpMax - postOpMax) / preOpMax) * 100);
    },

    // 计算模拟数据点数量
    countSimulatedData() {
      if (!this.simulatedResult) return 0;
      let count = 0;
      this.allMonths.forEach((month) => {
        if (!this.hasOriginalData(month)) {
          count++;
        }
      });
      return count;
    },
  },
};
</script>

<style scoped>
/* 管理按钮样式 */
.management-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.manage-btn,
.export-btn,
.import-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.manage-btn {
  background: #17a2b8;
  color: white;
}

.export-btn {
  background: #28a745;
  color: white;
}

.import-btn {
  background: #ffc107;
  color: #212529;
}

/* 数据管理区域样式 */
.data-management-section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f8f9fa;
}

.management-controls {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.add-btn,
.save-btn,
.reset-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.add-btn {
  background: #28a745;
  color: white;
}

.save-btn {
  background: #007bff;
  color: white;
}

.reset-btn {
  background: #dc3545;
  color: white;
}

.training-data-list {
  max-height: 500px;
  overflow-y: auto;
}

.patient-item {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 15px;
}

.patient-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.patient-name-input {
  padding: 5px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 16px;
  width: 200px;
}

.remove-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
}

.patient-data-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 10px;
}

.month-data-input {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.month-data-input label {
  font-size: 12px;
  font-weight: bold;
  color: #495057;
}

.dimension-input.small {
  width: 50px;
  padding: 4px;
  font-size: 12px;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.save-training-btn {
  background: #28a745;
  color: white;
  padding: 12px 25px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.save-training-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.save-training-btn:not(:disabled):hover {
  background: #218838;
  transform: translateY(-1px);
}

/* 其余样式保持不变 */
.patient-data-simulator {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  background: #f8f9fa;
  min-height: 100vh;
}

.input-section,
.new-patient-section,
.result-section {
  margin-bottom: 30px;
  padding: 25px;
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.stats {
  background: #f0f8ff;
  padding: 15px;
  border-radius: 8px;
}

.data-summary {
  display: flex;
  justify-content: space-around;
  margin-top: 15px;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 10px;
}

.summary-item .label {
  font-size: 0.9em;
  color: #666;
  margin-bottom: 5px;
}

.summary-item .value {
  font-weight: bold;
  color: #2c5aa0;
}

.month-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.month-input.preoperative {
  grid-column: 1 / -1;
  background: #fff9e6;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
}

.month-input {
  padding: 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fdfdfd;
}

.month-input label {
  font-weight: 600;
  margin-bottom: 10px;
  display: block;
  color: #333;
}

.dimension-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.dimension-input {
  width: 70px;
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
}

.dimension-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  outline: none;
}

.separator {
  color: #666;
  font-weight: bold;
}

.unit {
  color: #666;
  font-size: 14px;
}

.patient-input {
  margin-bottom: 20px;
}

.name-input {
  padding: 10px 15px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 16px;
  width: 300px;
  max-width: 100%;
}

.name-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  outline: none;
}

label {
  font-weight: bold;
  margin-bottom: 8px;
  display: inline-block;
  color: #333;
}

.simulate-btn,
.clear-btn {
  padding: 12px 25px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.simulate-btn {
  background: #007bff;
  color: white;
}

.simulate-btn:hover {
  background: #0056b3;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.clear-btn {
  background: #6c757d;
  color: white;
}

.clear-btn:hover {
  background: #545b62;
  transform: translateY(-1px);
}

.result-data {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 30px;
}

.result-item.preoperative {
  grid-column: 1 / -1;
  background: #fff9e6;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
  font-weight: bold;
}

.result-item {
  padding: 15px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  text-align: center;
  background: #fdfdfd;
  transition: transform 0.2s ease;
}

.result-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.month-label {
  font-weight: bold;
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-size: 1.1em;
}

.original-data {
  color: #28a745;
  font-weight: bold;
  font-size: 1.1em;
}

.simulated-data {
  color: #ff6b00;
  font-weight: bold;
  font-size: 1.1em;
}

.simulated-badge {
  font-size: 0.8em;
  color: #666;
  margin-left: 5px;
  background: #fff3e0;
  padding: 2px 6px;
  border-radius: 10px;
}

.trend-chart {
  margin-top: 30px;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 12px;
}

.chart-container {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 200px;
  margin-top: 20px;
  padding: 0 10px;
}

.chart-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  margin: 0 5px;
}

.bar-label {
  font-size: 12px;
  margin-bottom: 8px;
  text-align: center;
  font-weight: 600;
  color: #333;
}

.bar-container {
  height: 150px;
  width: 35px;
  background: #e9ecef;
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: flex-end;
  position: relative;
}

.bar {
  width: 100%;
  background: linear-gradient(to top, #007bff, #4dabf7);
  border-radius: 4px 4px 0 0;
  transition: height 0.5s ease;
  position: relative;
}

.bar.simulated-bar {
  background: linear-gradient(to top, #ff6b00, #ff922b);
}

.bar-value {
  font-size: 11px;
  margin-top: 8px;
  text-align: center;
  font-weight: 600;
  color: #333;
}

.data-stats {
  margin-top: 30px;
  padding: 20px;
  background: #e7f3ff;
  border-radius: 8px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: white;
  border-radius: 6px;
  border-left: 4px solid #007bff;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.stat-value {
  font-weight: bold;
  color: #2c5aa0;
  font-size: 16px;
}

.similar-patients {
  margin-bottom: 20px;
  padding: 15px;
  background: #e7f3ff;
  border-radius: 8px;
}

.similar-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.similar-patient {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #007bff;
  min-width: 100px;
}

.patient-name {
  font-weight: bold;
  color: #2c5aa0;
  margin-bottom: 3px;
}

.similarity {
  font-size: 0.8em;
  color: #666;
}

h2,
h3,
h4 {
  color: #2c3e50;
  margin-bottom: 20px;
}

h2 {
  text-align: center;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 15px;
  margin-bottom: 30px;
}
</style>
